import { useState, useEffect, useCallback, useRef } from 'react';
import { BookmarkCheck, Search, ScanLine, Plus, Trash2, Edit2, ArrowRight, Filter, Package, ChevronDown, ChevronRight, Copy, Layers, ArrowRightLeft, XCircle, Check, User, Users, Box, Phone, AlertCircle, MapPin, Clock, ShoppingCart, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import api from '../lib/api';
import { playSuccessSound, playErrorSound, getPriceWithoutTVA, getTVAAmount, formatPrice, roundPrice, calcLineTotal, normalizeBarcode } from '../lib/utils';

// Check if V3 (TVA mode) is active
const isV3 = () => localStorage.getItem('app_version') === 'v3';

// Check if customer ID is virtual (has more than 1 letter)
const isVirtualClient = (customerId) => {
    if (!customerId) return false;
    const letterCount = (customerId.match(/[a-zA-Z]/g) || []).length;
    return letterCount > 1;
};

// Get base customer ID (without -1, -2 suffix)
const getBaseCustomerId = (customerId) => {
    if (!customerId) return customerId;
    return customerId.replace(/-\d+$/, '');
};

// Check if customer has suffix (-1, -2, etc.)
const hasSuffix = (customerId) => {
    if (!customerId) return false;
    return /-\d+$/.test(customerId);
};

// LocalStorage key for checked customers
const CHECKED_CUSTOMERS_KEY = 'rezervari_checked_customers';

const RezervariV2 = () => {
    const [reservations, setReservations] = useState([]);
    const [summary, setSummary] = useState({ total_qty: 0, total_value: 0, unique_customers: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [boxSearch, setBoxSearch] = useState('');
    const [sortBy, setSortBy] = useState('value'); // 'value', 'box', 'date'
    const [showOldOnly, setShowOldOnly] = useState(false); // Filter for customers with reservations > 7 days
    
    // Checked customers state (persisted in localStorage)
    const [checkedCustomers, setCheckedCustomers] = useState(() => {
        try {
            const saved = localStorage.getItem(CHECKED_CUSTOMERS_KEY);
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });
    
    // Scanner state
    const [showScanner, setShowScanner] = useState(false);
    const [scanMode, setScanMode] = useState('store'); // 'store' or 'pallet'
    const [scannedProduct, setScannedProduct] = useState(null);
    
    // Expanded customer groups (for multi-box customers)
    const [expandedGroups, setExpandedGroups] = useState({});
    
    // Active pallet
    const [activePallet, setActivePallet] = useState(null);
    
    // Add reservation modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [customerId, setCustomerId] = useState('');
    const [productName, setProductName] = useState('');
    const [productBarcode, setProductBarcode] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [qty, setQty] = useState('1');
    const [price, setPrice] = useState('');
    const [reservationSource, setReservationSource] = useState('store'); // 'store' or 'pallet'
    const [productFound, setProductFound] = useState(false); // Track if product exists in DB
    
    // Customer autocomplete for virtual clients
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
    
    // Prevent double submission
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Box assignment modal for real clients
    const [showBoxModal, setShowBoxModal] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);
    const [boxNumber, setBoxNumber] = useState('');
    const [freeBoxes, setFreeBoxes] = useState([]);
    
    // Edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editQty, setEditQty] = useState('1');
    const [editPrice, setEditPrice] = useState('');
    
    // Store stock check
    const [storeStock, setStoreStock] = useState(null);
    
    // Min sold price info
    const [minSoldPrice, setMinSoldPrice] = useState(null);
    const [timesSold, setTimesSold] = useState(0);
    // Purchase price min info (for profit indicator)
    const [purchasePriceMin, setPurchasePriceMin] = useState(null);
    
    // Filters
    const [filterCustomer, setFilterCustomer] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showVirtualClients, setShowVirtualClients] = useState(false); // Hide virtual clients by default
    
    // Expanded customers state
    const [expandedCustomers, setExpandedCustomers] = useState({});
    
    // Move item to another customer modal
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [movingItem, setMovingItem] = useState(null);
    const [targetCustomerId, setTargetCustomerId] = useState('');
    const [isMoving, setIsMoving] = useState(false); // Prevent double-submit

    // Move ALL items from customer to another customer modal
    const [showMoveAllModal, setShowMoveAllModal] = useState(false);
    const [movingFromCustomer, setMovingFromCustomer] = useState(null);
    const [targetCustomerIdAll, setTargetCustomerIdAll] = useState('');
    
    // Customer boxes state (loaded with reservations)
    const [customerBoxes, setCustomerBoxes] = useState({});
    
    // Edit box modal
    const [showEditBoxModal, setShowEditBoxModal] = useState(false);
    const [editingBoxCustomer, setEditingBoxCustomer] = useState(null);
    const [editOldBox, setEditOldBox] = useState(null);
    const [editBoxNumber, setEditBoxNumber] = useState('');

    // Add extra box modal
    const [showAddExtraBoxModal, setShowAddExtraBoxModal] = useState(false);
    const [extraBoxCustomer, setExtraBoxCustomer] = useState(null);
    const [extraBoxNumber, setExtraBoxNumber] = useState('');
    const [extraBoxFreeBoxes, setExtraBoxFreeBoxes] = useState([]);
    const [nextAvailable, setNextAvailable] = useState({ numbered: null, m: null, l: null });
    const [occupiedMBoxes, setOccupiedMBoxes] = useState([]);
    const [occupiedLBoxes, setOccupiedLBoxes] = useState([]);
    const [freeMBoxes, setFreeMBoxes] = useState([]);
    const [freeLBoxes, setFreeLBoxes] = useState([]);

    // Virtual client box assignment modal
    const [showVirtualBoxModal, setShowVirtualBoxModal] = useState(false);
    const [boxAssignCustomerId, setBoxAssignCustomerId] = useState(null);
    const [boxInput, setBoxInput] = useState('');
    
    // Global search state (like Depozit)
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');
    const [globalSearchResults, setGlobalSearchResults] = useState({ customers: [], products: [], boxes: [] });
    const [showGlobalSearchResults, setShowGlobalSearchResults] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const globalSearchRef = useRef(null);

    // Copy modal state for mobile
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [copyText, setCopyText] = useState('');

    // Edit client modal state (for phone/name/address)
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [editingClientCustomer, setEditingClientCustomer] = useState(null);
    const [editClientName, setEditClientName] = useState('');
    const [editClientPhone, setEditClientPhone] = useState('');
    const [editClientEmail, setEditClientEmail] = useState('');
    const [editClientAddress, setEditClientAddress] = useState('');
    const [savingClient, setSavingClient] = useState(false);
    
    // Phone required modal - shown when adding product to customer without phone
    const [showPhoneRequiredModal, setShowPhoneRequiredModal] = useState(false);
    const [phoneRequiredCustomerId, setPhoneRequiredCustomerId] = useState('');
    const [phoneRequiredInput, setPhoneRequiredInput] = useState('');

    const loadData = useCallback(async () => {
        try {
            const [resRes, sumRes, palletRes, catRes] = await Promise.all([
                api.get('/reservations/v2'),
                api.get('/reservations/v2/summary'),
                api.get('/pallets/active'),
                api.get('/categories/v2')
            ]);
            const loadedReservations = resRes.data || [];
            setReservations(loadedReservations);
            setSummary(sumRes.data || { total_qty: 0, total_value: 0, unique_customers: 0 });
            setActivePallet(palletRes.data || null);
            setCategories(catRes.data || []);
            
            // Load box info for each REAL customer (not virtual)
            const boxPromises = loadedReservations
                .filter(r => !isVirtualClient(r.customer_id))
                .map(r => api.get(`/reservation-boxes/v2/customer/${r.customer_id}`).catch(() => ({ data: { has_box: false } })));
            
            const boxResults = await Promise.all(boxPromises);
            const boxMap = {};
            
            // Get virtual client IDs to fetch their location_box
            const virtualClientIds = loadedReservations
                .filter(r => isVirtualClient(r.customer_id))
                .map(r => r.customer_id);
            
            // Fetch location_box for virtual clients from the reservation data (it should include box_number now)
            loadedReservations.forEach((r, idx) => {
                if (!isVirtualClient(r.customer_id)) {
                    const realIdx = loadedReservations.slice(0, idx + 1).filter(x => !isVirtualClient(x.customer_id)).length - 1;
                    if (boxResults[realIdx]) {
                        boxMap[r.customer_id] = boxResults[realIdx].data;
                    }
                } else {
                    // Virtual clients - check if they have box_number from API response
                    if (r.box_number) {
                        boxMap[r.customer_id] = { 
                            is_virtual: true, 
                            has_box: true,
                            box_number: r.box_number,
                            box_message: `CUTIA ${r.box_number}`
                        };
                    } else {
                        boxMap[r.customer_id] = { is_virtual: true, has_box: false, box_message: "CLIENT LOCAL" };
                    }
                }
            });
            setCustomerBoxes(boxMap);
            
            // Clean up checked customers that no longer exist in reservations
            const currentCustomerIds = new Set(loadedReservations.map(r => r.customer_id));
            setCheckedCustomers(prev => {
                const cleaned = {};
                Object.keys(prev).forEach(customerId => {
                    if (currentCustomerIds.has(customerId)) {
                        cleaned[customerId] = prev[customerId];
                    }
                });
                // Only update localStorage if something changed
                if (Object.keys(cleaned).length !== Object.keys(prev).length) {
                    localStorage.setItem(CHECKED_CUSTOMERS_KEY, JSON.stringify(cleaned));
                }
                return cleaned;
            });
        } catch (err) {
            console.error('Error loading reservations:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);
    
    // Click outside to close global search results
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (globalSearchRef.current && !globalSearchRef.current.contains(event.target)) {
                setShowGlobalSearchResults(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Global search effect - searches customers, products, and boxes
    useEffect(() => {
        if (!globalSearchQuery.trim()) {
            setGlobalSearchResults({ customers: [], products: [], boxes: [] });
            setShowGlobalSearchResults(false);
            return;
        }
        
        const query = globalSearchQuery.toLowerCase().trim();
        const results = { customers: [], products: [], boxes: [] };
        
        // Search in reservations for customers and boxes
        reservations.forEach(reservation => {
            const customerId = reservation.customer_id || '';
            const customerName = reservation.customer_name || '';
            const phone = reservation.customer_phone || '';
            const boxNum = customerBoxes[customerId]?.box_message || customerBoxes[customerId]?.box?.box_number || '';
            
            // Search by customer ID, name, or phone
            if (customerId.toLowerCase().includes(query) || 
                customerName.toLowerCase().includes(query) ||
                phone.includes(query)) {
                // Check if customer already added
                if (!results.customers.find(c => c.customer_id === customerId)) {
                    results.customers.push({
                        customer_id: customerId,
                        customer_name: customerName,
                        phone: phone,
                        box_number: boxNum,
                        total_items: reservation.items?.length || 0,
                        total_value: reservation.total_value || 0,
                        is_virtual: isVirtualClient(customerId)
                    });
                }
            }
            
            // Search by box number
            if (boxNum && boxNum.toString().toLowerCase().includes(query)) {
                if (!results.boxes.find(b => b.customer_id === customerId)) {
                    results.boxes.push({
                        customer_id: customerId,
                        customer_name: customerName,
                        box_number: boxNum,
                        total_items: reservation.items?.length || 0
                    });
                }
            }
        });
        
        // Limit results
        results.customers = results.customers.slice(0, 5);
        results.boxes = results.boxes.slice(0, 3);
        
        setGlobalSearchResults(results);
        setShowGlobalSearchResults(
            results.customers.length > 0 || 
            results.products.length > 0 || 
            results.boxes.length > 0
        );
    }, [globalSearchQuery, reservations, customerBoxes]);
    
    // Search for product stock (FIFO order) when query looks like a barcode
    useEffect(() => {
        const searchProductStock = async () => {
            if (!globalSearchQuery.trim() || globalSearchQuery.length < 3) return;
            
            const query = globalSearchQuery.toUpperCase().trim();
            
            // Check if it looks like a barcode (mostly numbers or starts with letter+numbers)
            const looksLikeBarcode = /^[A-Z0-9]{3,}$/i.test(query);
            if (!looksLikeBarcode) return;
            
            try {
                setSearchLoading(true);
                
                // Fetch stock info from multiple sources
                const [storeRes, depotRes, priceRes] = await Promise.all([
                    api.get('/store/v2').catch(() => ({ data: [] })),
                    api.get('/depot/v2/pallets').catch(() => ({ data: [] })),
                    api.get(`/depot/v2/product-price/${query}`).catch(() => ({ data: {} }))
                ]);
                
                const storeItems = storeRes.data || [];
                const pallets = depotRes.data || [];
                
                // Find product in store
                const storeProduct = storeItems.find(item => 
                    item.barcode?.toUpperCase().includes(query) ||
                    item.name?.toLowerCase().includes(query.toLowerCase())
                );
                
                // Find product in pallets (FIFO - sorted by entry_timestamp)
                const palletLocations = [];
                pallets.forEach(pallet => {
                    (pallet.boxes || []).forEach(box => {
                        (box.items || []).forEach(item => {
                            if (item.barcode?.toUpperCase().includes(query) ||
                                item.name?.toLowerCase().includes(query.toLowerCase())) {
                                palletLocations.push({
                                    pallet_id: pallet.id,
                                    pallet_number: pallet.pallet_id || pallet.number,
                                    barcode: item.barcode,
                                    name: item.name,
                                    quantity: item.quantity || 0,
                                    entry_timestamp: item.entry_timestamp || item.created_at || pallet.created_at
                                });
                            }
                        });
                    });
                });
                
                // Sort by entry_timestamp (FIFO - oldest first)
                palletLocations.sort((a, b) => {
                    const dateA = new Date(a.entry_timestamp || '2020-01-01');
                    const dateB = new Date(b.entry_timestamp || '2020-01-01');
                    return dateA - dateB;
                });
                
                // Check if product is in other reservations
                const reservedInOrders = [];
                reservations.forEach(res => {
                    (res.items || []).forEach(item => {
                        if (item.barcode?.toUpperCase().includes(query)) {
                            reservedInOrders.push({
                                customer_id: res.customer_id,
                                customer_name: res.customer_name,
                                quantity: item.quantity || 0
                            });
                        }
                    });
                });
                
                // Build product result
                if (storeProduct || palletLocations.length > 0) {
                    const productResult = {
                        barcode: storeProduct?.barcode || palletLocations[0]?.barcode || query,
                        name: storeProduct?.name || palletLocations[0]?.name || query,
                        store_qty: storeProduct?.quantity || 0,
                        store_entry: storeProduct?.entry_timestamp || storeProduct?.created_at,
                        pallet_locations: palletLocations,
                        reserved_in: reservedInOrders,
                        pret_mediu_min: priceRes.data?.pret_mediu_min || 0,
                        pret_vanzare_min: priceRes.data?.pret_vanzare_min || 0
                    };
                    
                    setGlobalSearchResults(prev => ({
                        ...prev,
                        products: [productResult]
                    }));
                    setShowGlobalSearchResults(true);
                }
            } catch (err) {
                console.error('Error searching product stock:', err);
            } finally {
                setSearchLoading(false);
            }
        };
        
        // Debounce the product search
        const timer = setTimeout(searchProductStock, 300);
        return () => clearTimeout(timer);
    }, [globalSearchQuery, reservations]);
    
    // Navigate to customer reservation
    const navigateToCustomer = (customerId) => {
        setShowGlobalSearchResults(false);
        setGlobalSearchQuery('');
        setSearch(customerId);
        
        // Expand the customer if collapsed
        setExpandedCustomers(prev => ({ ...prev, [customerId]: true }));
        
        // Scroll to customer after a short delay
        setTimeout(() => {
            const customerElement = document.querySelector(`[data-customer-id="${customerId}"]`);
            if (customerElement) {
                customerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                customerElement.classList.add('ring-2', 'ring-amber-500');
                setTimeout(() => {
                    customerElement.classList.remove('ring-2', 'ring-amber-500');
                }, 3000);
            }
        }, 100);
    };

    // Handle barcode scan
    const handleScannedBarcode = async (barcode) => {
        setShowScanner(false);
        try {
            // Normalize barcode: remove spaces, limit to 16 chars, uppercase
            const normalizedBarcode = normalizeBarcode(barcode);
            
            // Try lookup endpoint first (searches everywhere)
            const lookupRes = await api.get(`/products/lookup/${normalizedBarcode}`);
            
            if (lookupRes.data?.found && lookupRes.data?.product) {
                const product = lookupRes.data.product;
                setProductBarcode(product.barcode || normalizedBarcode);
                setProductName(product.name || '');
                setProductCategory(product.category || '');
                setProductFound(true); // Product exists
                
                // Set min sold price info
                setMinSoldPrice(lookupRes.data.min_sold_price || null);
                setTimesSold(lookupRes.data.times_sold || 0);
                
                // Fetch purchase price min for profit indicator
                try {
                    const priceHistoryRes = await api.get(`/depot/v2/product-price/${normalizedBarcode}`);
                    setPurchasePriceMin(priceHistoryRes.data?.pret_mediu_min || null);
                } catch (e) {
                    setPurchasePriceMin(null);
                }
            } else {
                // Fallback - search in products_v2
                const productsRes = await api.get('/products/v2');
                const products = productsRes.data || [];
                const product = products.find(p => p.barcode?.toUpperCase() === normalizedBarcode);
                
                if (product) {
                    setProductBarcode(product.barcode);
                    setProductName(product.name);
                    setProductCategory(product.category || '');
                    setProductFound(true); // Product exists
                } else {
                    setProductBarcode(barcode);
                    setProductName('');
                    setProductCategory('');
                    setProductFound(false); // New product
                }
                setMinSoldPrice(null);
                setTimesSold(0);
                setPurchasePriceMin(null);
            }
            
            // Fetch last used price for this product
            try {
                const priceRes = await api.get(`/products/last-price/${normalizedBarcode}`);
                const lastPrice = priceRes.data?.last_price || 0;
                setPrice(lastPrice.toString());
            } catch (e) {
                setPrice('0');
            }
            
            // Set reservation source based on scan mode
            setReservationSource(scanMode);
            setStoreStock(null); // Reset store stock
            setShowAddModal(true);
            
            // After scan, focus on client field
            setTimeout(() => {
                document.getElementById('reservation-customer-id')?.focus();
            }, 100);
        } catch (err) {
            setProductBarcode(barcode);
            setProductFound(false);
            setReservationSource(scanMode);
            setStoreStock(null); // Reset store stock
            setShowAddModal(true);
            
            // Focus on client field
            setTimeout(() => {
                document.getElementById('reservation-customer-id')?.focus();
            }, 100);
        }
    };
    
    // Open scanner for store reservation
    const openStoreScanner = () => {
        setScanMode('store');
        setShowScanner(true);
    };
    
    // Open scanner for pallet reservation
    const openPalletScanner = () => {
        if (!activePallet) {
            playErrorSound();
            toast.error(
                <div>
                    <p className="font-medium">Nu există palet deschis!</p>
                    <p className="text-sm mt-1">Mergi la "Recepție Palet" și deschide un palet nou.</p>
                </div>,
                { duration: 5000 }
            );
            return;
        }
        setScanMode('pallet');
        setShowScanner(true);
    };

    // Toggle customer check (persisted in localStorage)
    const toggleCustomerCheck = (customerId) => {
        setCheckedCustomers(prev => {
            const newState = { ...prev };
            if (newState[customerId]) {
                delete newState[customerId];
            } else {
                newState[customerId] = true;
            }
            localStorage.setItem(CHECKED_CUSTOMERS_KEY, JSON.stringify(newState));
            return newState;
        });
    };

    // Get customer suggestions for autocomplete (virtual clients only)
    const getCustomerSuggestions = (input) => {
        if (!input || input.length < 1) {
            setCustomerSuggestions([]);
            setShowCustomerSuggestions(false);
            return;
        }
        const inputUpper = input.toUpperCase();
        // Get unique virtual customer IDs from reservations
        const virtualCustomers = reservations
            .filter(r => isVirtualClient(r.customer_id))
            .map(r => r.customer_id);
        
        const matches = virtualCustomers.filter(c => 
            c.startsWith(inputUpper) && c !== inputUpper
        );
        
        setCustomerSuggestions(matches.slice(0, 5)); // Max 5 suggestions
        setShowCustomerSuggestions(matches.length > 0);
    };

    // Select a customer from suggestions
    const selectCustomerSuggestion = (suggestion) => {
        setCustomerId(suggestion);
        setShowCustomerSuggestions(false);
        // Move focus to product name or price
        setTimeout(() => {
            if (!productName.trim()) {
                document.getElementById('reservation-product-name')?.focus();
            } else {
                document.getElementById('reservation-price')?.focus();
            }
        }, 50);
    };

    // Open add modal for a specific customer (from + button)
    const openAddModalForCustomer = (customerIdToAdd) => {
        setCustomerId(customerIdToAdd);
        setProductBarcode('');
        setProductName('');
        setProductCategory('');
        setQty('1');
        setPrice('0');
        setReservationSource('store');
        setStoreStock(null);
        setProductFound(false); // Reset product found state
        setShowAddModal(true);
        // Focus on barcode input after modal opens
        setTimeout(() => {
            document.getElementById('reservation-barcode')?.focus();
        }, 100);
    };

    // Consolidate duplicate reservations
    const handleConsolidateDuplicates = async () => {
        try {
            const res = await api.post('/reservations/v2/merge-duplicates');
            if (res.data.merged_groups > 0) {
                toast.success(res.data.message);
                loadData(); // Refresh the data
            } else {
                toast.info('Nu există duplicate de combinat');
            }
        } catch (err) {
            toast.error('Eroare la consolidare');
            console.error(err);
        }
    };

    // Toggle customer expansion
    const toggleCustomer = (customerId) => {
        setExpandedCustomers(prev => ({
            ...prev,
            [customerId]: !prev[customerId]
        }));
    };

    // Move item to another customer
    const openMoveModal = (item) => {
        setMovingItem(item);
        setTargetCustomerId('');
        setShowMoveModal(true);
    };

    const handleMoveItem = async () => {
        if (!movingItem || !targetCustomerId.trim()) {
            toast.error('Introdu ID-ul clientului destinație');
            return;
        }
        
        // Prevent double-submit
        if (isMoving) {
            console.log('[MOVE] Already moving, ignoring');
            return;
        }
        
        setIsMoving(true);
        try {
            await api.post(`/reservations/v2/move-item/${movingItem.id}`, {
                target_customer_id: targetCustomerId.trim().toUpperCase()
            });
            toast.success(`Produs mutat la clientul ${targetCustomerId.toUpperCase()}`);
            setShowMoveModal(false);
            setMovingItem(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        } finally {
            setIsMoving(false);
        }
    };

    // Open modal to move ALL reservations from a customer to another
    const openMoveAllModal = (customerId, reservation) => {
        setMovingFromCustomer({ customerId, itemCount: reservation.items?.length || 0, totalValue: reservation.total_value });
        setTargetCustomerIdAll('');
        setShowMoveAllModal(true);
    };

    // Move ALL reservations from one customer to another
    const handleMoveAllToCustomer = async () => {
        if (!movingFromCustomer || !targetCustomerIdAll.trim()) {
            toast.error('Introdu ID-ul clientului destinație');
            return;
        }
        
        // Prevent double-submit
        if (isMoving) {
            console.log('[MOVE ALL] Already moving, ignoring');
            return;
        }
        
        setIsMoving(true);
        try {
            const res = await api.post(`/reservations/v2/move-all/${movingFromCustomer.customerId}`, {
                target_customer_id: targetCustomerIdAll.trim().toUpperCase()
            });
            playSuccessSound();
            toast.success(res.data.message);
            setShowMoveAllModal(false);
            setMovingFromCustomer(null);
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        } finally {
            setIsMoving(false);
        }
    };

    // Delete all reservations for a customer (return to store)
    const handleDeleteAllReservations = async (customerId) => {
        const isVirtual = isVirtualClient(customerId);
        const message = isVirtual 
            ? `Sigur vrei să ștergi TOATE rezervările pentru ${customerId}?`
            : `Sigur vrei să ștergi TOATE rezervările pentru ${customerId}?\n\nProdusele vor fi returnate în magazin.`;
        
        if (!window.confirm(message)) {
            return;
        }
        try {
            const res = await api.delete(`/reservations/v2/customer/${customerId}/all`);
            toast.success(res.data.message || `Rezervări șterse`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la ștergere');
        }
    };

    // Complete/finalize a virtual reservation (moves to history)
    const handleCompleteVirtualReservation = async (customerId) => {
        if (!window.confirm(`Finalizezi rezervarea pentru ${customerId}?\nVa fi mutată în istoricul Comenzi Neconfirmate.`)) {
            return;
        }
        try {
            const res = await api.post(`/reservations/v2/complete-virtual/${customerId}`);
            playSuccessSound();
            toast.success(res.data.message || 'Rezervare finalizată');
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la finalizare');
        }
    };

    // Open edit client modal
    const openEditClientModal = (customer) => {
        setEditingClientCustomer(customer);
        setEditClientName(customer.name || '');
        setEditClientPhone(customer.phone || '');
        setEditClientEmail(customer.email || '');
        setEditClientAddress(customer.address || '');
        setShowEditClientModal(true);
    };

    // Save client data (phone, name, email, address)
    const handleSaveClient = async () => {
        if (!editingClientCustomer) return;
        setSavingClient(true);
        try {
            await api.put(`/reservations/v2/customers/${editingClientCustomer.customer_id}/update-info`, {
                name: editClientName.trim(),
                phone: editClientPhone.trim(),
                email: editClientEmail.trim(),
                address: editClientAddress.trim()
            });
            toast.success('Date client salvate!');
            setShowEditClientModal(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvarea datelor');
        } finally {
            setSavingClient(false);
        }
    };

    // Toggle group expansion (for multi-box customers)
    const toggleGroup = (baseCustomerId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [baseCustomerId]: !prev[baseCustomerId]
        }));
    };

    // Copy reservation list - simple approach with modal fallback
    const handleCopyReservation = async (reservation) => {
        const items = reservation.items || [];
        const customerId = reservation.customer_id || '';
        
        // Check if virtual client (more than 1 letter in ID)
        const letterCount = (customerId.match(/[a-zA-Z]/g) || []).length;
        const isVirtual = letterCount > 1;
        
        // Separate regular items from gifts
        const regularItems = items.filter(item => !item.is_gift && item.source !== 'gift');
        const giftItems = items.filter(item => item.is_gift || item.source === 'gift');
        
        let text = `Rezervări pentru: ${customerId}\n\n`;
        let lineNum = 1;
        let subtotal = 0;
        
        // Regular items with prices
        regularItems.forEach(item => {
            const lineTotal = calcLineTotal(item.quantity, item.unit_price);
            subtotal += lineTotal;
            text += `${lineNum}. ${item.name} x${item.quantity} = ${formatPrice(lineTotal)} RON\n`;
            lineNum++;
        });
        
        // Gift items - separated, without price
        if (giftItems.length > 0) {
            text += `\nCADOU\n`;
            giftItems.forEach(item => {
                if (item.quantity > 1) {
                    text += `${item.name} x${item.quantity}\n`;
                } else {
                    text += `${item.name}\n`;
                }
            });
        }
        
        // Add transport fee for normal clients only
        let transportFee = 0;
        if (!isVirtual) {
            try {
                const settingsRes = await api.get('/settings/v2');
                transportFee = settingsRes.data?.transport_fee || 25;
            } catch (e) {
                transportFee = 25; // Default
            }
            text += `\nTransport = ${formatPrice(transportFee)} RON`;
            text += `\nTOTAL: ${formatPrice(roundPrice(subtotal + transportFee))} RON`;
        } else {
            text += `\nTOTAL: ${formatPrice(roundPrice(subtotal))} RON`;
        }
        
        // Check if mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        
        // On mobile, use Share API (works best on iOS/Android)
        if (isMobile && navigator.share) {
            try {
                await navigator.share({ text: text });
                return;
            } catch (err) {
                if (err.name === 'AbortError') return; // User cancelled
            }
        }
        
        // Try clipboard (works on desktop, sometimes on mobile)
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Lista copiată!');
            return;
        } catch (err) {
            // Clipboard failed
        }
        
        // Fallback: show modal with text for manual copy
        setCopyText(text);
        setShowCopyModal(true);
    };

    // Add reservation
    const handleAddReservation = async () => {
        // Prevent double submission
        if (isSubmitting) return;
        
        if (!customerId.trim()) {
            playErrorSound();
            toast.error('Introdu ID client');
            return;
        }
        if (!productBarcode.trim()) {
            playErrorSound();
            toast.error('Scanează sau introdu codul produsului');
            return;
        }
        if (!productName.trim()) {
            playErrorSound();
            toast.error('Introdu numele produsului');
            return;
        }
        if (!price || price === '') {
            playErrorSound();
            toast.error('Introdu prețul');
            return;
        }
        
        // Check if price is 0 - ask for gift confirmation
        // NOTE: Negative prices are allowed (for advances/avansuri)
        const priceValue = parseFloat(price);
        let isGift = false;
        if (priceValue === 0) {
            const confirmGift = window.confirm('Prețul este 0. Adaugi ca și CADOU?\n\n(Nu se scade din stoc, nu apare în facturi)');
            if (!confirmGift) {
                toast.info('Introdu un preț sau confirmă cadou');
                return;
            }
            isGift = true;
        }

        setIsSubmitting(true);
        
        try {
            // If gift, use gift endpoint (doesn't deduct from pallet/store)
            if (isGift) {
                const customerUpper = customerId.toUpperCase();
                const giftPayload = {
                    customer_id: customerUpper,
                    barcode: productBarcode.toUpperCase(),
                    name: productName,
                    category: productCategory || '',
                    quantity: parseInt(qty) || 1
                };
                
                // Check if this is a REAL client (not virtual) - announce box
                const letterCount = (customerUpper.match(/[a-zA-Z]/g) || []).length;
                const isRealClient = letterCount <= 1;
                
                if (isRealClient) {
                    // Check if customer has a box
                    const boxRes = await api.get(`/reservation-boxes/v2/customer/${customerUpper}`);
                    
                    if (boxRes.data.has_box) {
                        // Customer has box - save gift and announce
                        await api.post(`/reservations/v2/add-gift`, giftPayload);
                        
                        const boxes = boxRes.data.boxes || [boxRes.data.box];
                        const lastBox = boxes[boxes.length - 1];
                        const boxNum = lastBox?.box_number;
                        const boxMessage = `CUTIA ${boxNum}`;
                        
                        playSuccessSound();
                        toast.success(`🎁 CADOU adăugat: ${productName} - ${boxMessage}`, { duration: 5000 });
                        
                        if (String(boxNum).toUpperCase() === 'F') {
                            speakMessage("Cadou. Fără cutie");
                        } else {
                            speakMessage(`Cadou. ${boxMessage}`);
                        }
                        finishAddReservation();
                    } else {
                        // Customer needs a box - show modal FIRST, save gift AFTER
                        const freeRes = await api.get('/reservation-boxes/v2/free');
                        const data = freeRes.data || {};
                        setFreeBoxes(data.free_boxes || []);
                        setFreeMBoxes(data.free_m_boxes || []);
                        setFreeLBoxes(data.free_l_boxes || []);
                        setOccupiedMBoxes(data.occupied_m || []);
                        setOccupiedLBoxes(data.occupied_l || []);
                        setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
                        // Save gift payload - will be saved when box is confirmed
                        setPendingReservation({ customerId: customerUpper, giftPayload: giftPayload });
                        setBoxNumber('');
                        setShowAddModal(false);
                        setShowBoxModal(true);
                    }
                } else {
                    // Virtual client - save gift and announce
                    await api.post(`/reservations/v2/add-gift`, giftPayload);
                    playSuccessSound();
                    toast.success(`🎁 CADOU adăugat: ${productName} - CLIENT LOCAL`, { duration: 4000 });
                    speakMessage("Cadou. Client local");
                    finishAddReservation();
                }
                return;
            }
            
            const payload = {
                customer_id: customerId.toUpperCase(),
                barcode: productBarcode.toUpperCase(),
                name: productName,
                category: productCategory || '',
                quantity: parseInt(qty) || 1,
                unit_price: parseFloat(price),
                source: reservationSource
            };
            
            // Add pallet_id if reserving from pallet
            if (reservationSource === 'pallet' && activePallet) {
                payload.pallet_id = activePallet.id;
            }
            
            // Check if this is a REAL client (not virtual) - needs box assignment
            const customerUpper = customerId.toUpperCase();
            const letterCount = (customerUpper.match(/[a-zA-Z]/g) || []).length;
            const isRealClient = letterCount <= 1;
            
            if (isRealClient) {
                // Check if customer already has a box
                const boxRes = await api.get(`/reservation-boxes/v2/customer/${customerUpper}`);
                
                if (boxRes.data.has_box) {
                    // Customer already has a box - save reservation and announce box
                    const addRes = await api.post('/reservations/v2/add', payload);
                    
                    const boxes = boxRes.data.boxes || [boxRes.data.box];
                    const lastBox = boxes[boxes.length - 1];
                    const boxNum = lastBox?.box_number;
                    const boxMessage = boxRes.data.box_message || `CUTIA ${boxNum}`;
                    playSuccessSound();
                    toast.success(boxMessage, { duration: 5000 });
                    
                    if (String(boxNum).toUpperCase() === 'F') {
                        speakMessage("Fără cutie");
                    } else {
                        speakBoxNumber(boxNum);
                    }
                    
                    finishAddReservation(addRes.data, customerUpper);
                } else {
                    // Customer needs a box - show modal FIRST, save reservation AFTER box is selected
                    const freeRes = await api.get('/reservation-boxes/v2/free');
                    const data = freeRes.data || {};
                    setFreeBoxes(data.free_boxes || []);
                    setFreeMBoxes(data.free_m_boxes || []);
                    setFreeLBoxes(data.free_l_boxes || []);
                    setOccupiedMBoxes(data.occupied_m || []);
                    setOccupiedLBoxes(data.occupied_l || []);
                    setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
                    // Save the FULL payload, not just customerId - we'll add reservation when box is confirmed
                    setPendingReservation({ customerId: customerUpper, payload: payload });
                    setBoxNumber('');
                    setShowAddModal(false);
                    setShowBoxModal(true);
                    // Don't save reservation yet - will be saved in handleAssignBox
                }
            } else {
                // Virtual client - check if they have a box assigned (location_box)
                // First get customer info to check for location_box
                let customerBox = null;
                try {
                    const customerRes = await api.get(`/customers/v2/by-id/${customerUpper}`);
                    customerBox = customerRes.data?.location_box;
                } catch {
                    // Customer might not exist yet, that's ok
                }
                
                const addRes = await api.post('/reservations/v2/add', payload);
                playSuccessSound();
                
                if (customerBox) {
                    // Has box assigned - announce box number then "client local"
                    toast.success(`CUTIA ${customerBox} - CLIENT LOCAL`, { duration: 5000 });
                    speakBoxNumber(customerBox);
                    setTimeout(() => {
                        speakMessage("client local");
                    }, 1000);
                } else {
                    // No box - just announce "client local" and prompt for box
                    toast.success('CLIENT LOCAL - ATRIBUIE CUTIE!', { duration: 5000 });
                    speakMessage("Client local. Atribuie cutie.");
                    
                    // Open box assignment modal for virtual client
                    try {
                        const freeRes = await api.get('/reservation-boxes/v2/free');
                        const data = freeRes.data || {};
                        setFreeMBoxes(data.free_m_boxes || []);
                        setFreeLBoxes(data.free_l_boxes || []);
                        setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
                    } catch {
                        // Ignore errors loading boxes
                    }
                    setBoxAssignCustomerId(customerUpper);
                    setBoxInput('');
                    setShowVirtualBoxModal(true);
                }
                
                finishAddReservation(addRes.data, customerUpper);
            }
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            setIsSubmitting(false);
        }
    };

    // Speak box number using Web Speech API
    const speakBoxNumber = (boxNum) => {
        if ('speechSynthesis' in window) {
            let textToSpeak = '';
            const boxStr = String(boxNum).toUpperCase().trim();
            
            // Handle M and L prefixed boxes
            if (boxStr.startsWith('M')) {
                const num = boxStr.substring(1);
                textToSpeak = `M ${num}`;
            } else if (boxStr.startsWith('L')) {
                const num = boxStr.substring(1);
                textToSpeak = `L ${num}`;
            } else if (boxStr === 'F') {
                textToSpeak = 'Fără cutie';
            } else {
                textToSpeak = `Cutia ${boxStr}`;
            }
            
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(textToSpeak);
            utterance.lang = 'ro-RO';
            utterance.rate = 0.9;
            utterance.pitch = 1;
            utterance.volume = 1;
            window.speechSynthesis.speak(utterance);
        }
    };

    // Finish the add reservation flow
    const finishAddReservation = (response = null, customerIdForPhone = null) => {
        // Check if customer needs phone - show modal
        if (response?.customer_needs_phone && customerIdForPhone) {
            setPhoneRequiredCustomerId(customerIdForPhone);
            setPhoneRequiredInput('');
            setShowPhoneRequiredModal(true);
        }
        
        setShowAddModal(false);
        setCustomerId('');
        setProductBarcode('');
        setProductName('');
        setProductCategory('');
        setQty('1');
        setPrice('');
        setIsSubmitting(false);
        setProductFound(false); // Reset product found state
        
        loadData();
        
        // Reopen scanner with same mode
        setScanMode(reservationSource);
        setShowScanner(true);
    };

    // Assign box to customer
    const handleAssignBox = async () => {
        if (!pendingReservation || !boxNumber) {
            toast.error('Introdu numărul cutiei sau F pentru fără cutie');
            return;
        }
        
        try {
            // Accept either number or "F" for no box
            const boxValue = boxNumber.toUpperCase().trim();
            
            // First, save the reservation/gift if we have a pending payload
            let addRes = null;
            if (pendingReservation.payload) {
                addRes = await api.post('/reservations/v2/add', pendingReservation.payload);
            } else if (pendingReservation.giftPayload) {
                addRes = await api.post('/reservations/v2/add-gift', pendingReservation.giftPayload);
            }
            
            // Then assign the box
            const res = await api.post('/reservation-boxes/v2/assign', {
                customer_id: pendingReservation.customerId,
                box_number: boxValue
            });
            
            playSuccessSound();
            const message = res.data.box_message || `CUTIA ${boxValue}`;
            
            // Different toast for gift vs regular
            if (pendingReservation.giftPayload) {
                toast.success(`🎁 CADOU adăugat - ${message}`, { duration: 5000 });
                if (boxValue === 'F') {
                    speakMessage("Cadou. Fără cutie");
                } else {
                    speakMessage(`Cadou. ${message}`);
                }
            } else {
                toast.success(message, { duration: 5000 });
                if (boxValue === 'F') {
                    speakMessage("Fără cutie");
                } else {
                    speakBoxNumber(boxValue);
                }
            }
            
            const savedCustomerId = pendingReservation.customerId;
            setShowBoxModal(false);
            setPendingReservation(null);
            setBoxNumber('');
            finishAddReservation(addRes?.data, savedCustomerId);
        } catch (err) {
            playErrorSound();
            const errorMsg = err.response?.data?.detail || 'Eroare la atribuire cutie';
            toast.error(errorMsg, { duration: 5000 });
            
            // If conflict (409), keep modal open and clear input for retry
            if (err.response?.status === 409) {
                setBoxNumber(''); // Clear for new input
                // Modal stays open - user can enter a different box
            }
        }
    };
    
    // Speak any message using Web Speech API
    const speakMessage = (message) => {
        if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(message);
            utterance.lang = 'ro-RO';
            utterance.rate = 0.9;
            utterance.pitch = 1;
            utterance.volume = 1;
            window.speechSynthesis.speak(utterance);
        }
    };
    
    // Save phone number for customer (from phone required modal)
    const handleSavePhoneRequired = async () => {
        if (!phoneRequiredCustomerId || !phoneRequiredInput.trim()) {
            toast.error('Introdu numărul de telefon');
            return;
        }
        try {
            await api.put(`/reservations/v2/customers/${phoneRequiredCustomerId}/update-info`, {
                phone: phoneRequiredInput.trim()
            });
            toast.success('Telefon salvat');
            setShowPhoneRequiredModal(false);
            setPhoneRequiredInput('');
            setPhoneRequiredCustomerId('');
            loadData(); // Refresh to update card colors
        } catch (err) {
            toast.error('Eroare la salvare telefon');
            console.error(err);
        }
    };
    
    // Open edit box modal
    const openEditBoxModal = async (customerId) => {
        const boxInfo = customerBoxes[customerId];
        setEditingBoxCustomer(customerId);
        const realBoxes = (boxInfo?.boxes || []).filter(b => String(b.box_number).toUpperCase() !== 'F');
        const firstBox = realBoxes[0]?.box_number ?? boxInfo?.box?.box_number;
        setEditOldBox(realBoxes.length > 1 ? String(firstBox) : null);
        setEditBoxNumber(firstBox != null ? String(firstBox) : '');
        // Load free boxes
        try {
            const freeRes = await api.get('/reservation-boxes/v2/free');
            const data = freeRes.data || {};
            setFreeBoxes(data.free_boxes || []);
            setFreeMBoxes(data.free_m_boxes || []);
            setFreeLBoxes(data.free_l_boxes || []);
            setOccupiedMBoxes(data.occupied_m || []);
            setOccupiedLBoxes(data.occupied_l || []);
            setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
        } catch (e) {
            setFreeBoxes([]);
            setFreeMBoxes([]);
            setFreeLBoxes([]);
        }
        setShowEditBoxModal(true);
    };
    
    // Save edited box
    const handleSaveBox = async () => {
        if (!editingBoxCustomer || !editBoxNumber.trim()) {
            toast.error('Introdu numărul cutiei sau F pentru fără cutie');
            return;
        }
        
        try {
            const boxValue = editBoxNumber.toUpperCase().trim();
            const res = await api.post('/reservation-boxes/v2/assign', {
                customer_id: editingBoxCustomer,
                box_number: boxValue,
                replace: true,
                old_box_number: editOldBox
            });
            
            playSuccessSound();
            const message = res.data.box_message || `CUTIA ${boxValue}`;
            toast.success(message, { duration: 3000 });
            
            // Speak the box
            if (boxValue === 'F') {
                speakMessage("Fără cutie");
            } else {
                speakBoxNumber(boxValue);
            }
            
            setShowEditBoxModal(false);
            setEditingBoxCustomer(null);
            setEditBoxNumber('');
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la salvare cutie');
        }
    };

    // Open add extra box modal
    const openAddExtraBoxModal = async (customerId) => {
        setExtraBoxCustomer(customerId);
        setExtraBoxNumber('');
        try {
            const freeRes = await api.get('/reservation-boxes/v2/free');
            const data = freeRes.data || {};
            setExtraBoxFreeBoxes(data.free_boxes || []);
            setFreeMBoxes(data.free_m_boxes || []);
            setFreeLBoxes(data.free_l_boxes || []);
            setOccupiedMBoxes(data.occupied_m || []);
            setOccupiedLBoxes(data.occupied_l || []);
            setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
        } catch {
            setExtraBoxFreeBoxes([]);
        }
        setShowAddExtraBoxModal(true);
    };

    // Add extra box to customer
    const handleAddExtraBox = async () => {
        if (!extraBoxCustomer || !extraBoxNumber.trim()) {
            toast.error('Introdu numărul cutiei suplimentare');
            return;
        }
        
        try {
            const boxValue = extraBoxNumber.toUpperCase().trim();
            const res = await api.post('/reservation-boxes/v2/add-extra', {
                customer_id: extraBoxCustomer,
                box_number: boxValue
            });
            
            playSuccessSound();
            const message = res.data.box_message || `CUTIA ${boxValue} adăugată`;
            toast.success(message, { duration: 3000 });
            
            // Speak the box
            if (boxValue === 'F') {
                speakMessage("Fără cutie");
            } else {
                speakBoxNumber(boxValue);
            }
            
            setShowAddExtraBoxModal(false);
            setExtraBoxCustomer(null);
            setExtraBoxNumber('');
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la adăugare cutie');
        }
    };

    // Assign box to virtual client
    const handleAssignVirtualBox = async () => {
        if (!boxAssignCustomerId || !boxInput.trim()) {
            toast.error('Introdu numărul cutiei');
            return;
        }
        
        try {
            const boxValue = boxInput.toUpperCase().trim();
            await api.post('/order-boxes/assign', {
                customer_id: boxAssignCustomerId,
                box_number: boxValue
            });
            
            playSuccessSound();
            toast.success(`Cutia ${boxValue} alocată pentru ${boxAssignCustomerId}`, { duration: 3000 });
            
            // Speak the box number then "client local"
            speakBoxNumber(boxValue);
            setTimeout(() => {
                speakMessage("client local");
            }, 1000);
            
            setShowVirtualBoxModal(false);
            setBoxAssignCustomerId(null);
            setBoxInput('');
            loadData();
        } catch (err) {
            playErrorSound();
            const errorMsg = err.response?.data?.detail || 'Eroare la alocare cutie';
            toast.error(errorMsg, { duration: 5000 });
            
            // If conflict (409), keep modal open and clear input for retry
            if (err.response?.status === 409) {
                setBoxInput(''); // Clear for new input
                // Modal stays open - user can enter a different box
            }
            // For other errors, also keep modal open
        }
    };

    // Open virtual box modal and load available boxes
    const openVirtualBoxModal = async (customerId, currentBox = '') => {
        setBoxAssignCustomerId(customerId);
        setBoxInput(currentBox);
        
        // Load available M/L boxes
        try {
            const res = await api.get('/reservation-boxes/v2/free');
            const data = res.data;
            setFreeMBoxes(data.free_m_boxes || []);
            setFreeLBoxes(data.free_l_boxes || []);
            setNextAvailable(data.next_available || { numbered: null, m: null, l: null });
        } catch {
            setFreeMBoxes([]);
            setFreeLBoxes([]);
        }
        
        setShowVirtualBoxModal(true);
    };

    // Edit reservation
    const handleEditReservation = async () => {
        if (!editingItem) return;
        try {
            const res = await api.put(`/reservations/v2/items/${editingItem.id}`, {
                quantity: parseInt(editQty),
                unit_price: parseFloat(editPrice)
            });
            playSuccessSound();
            toast.success('Rezervare actualizată');
            // Show stock transparency message
            if (res.data?.stock_message) {
                toast.info(res.data.stock_message, { duration: 4000 });
            }
            setShowEditModal(false);
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error('Eroare la actualizare');
        }
    };

    // Delete reservation
    const handleDeleteReservation = async (itemId) => {
        if (!window.confirm('Ștergi această rezervare?')) return;
        try {
            const res = await api.delete(`/reservations/v2/items/${itemId}`);
            toast.success('Rezervare ștearsă');
            // Show stock transparency message
            if (res.data?.stock_message) {
                toast.info(res.data.stock_message, { duration: 4000 });
            }
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
        }
    };

    // Move to order
    const handleMoveToOrder = async (itemId) => {
        try {
            await api.post(`/reservations/v2/move-to-order/${itemId}`);
            playSuccessSound();
            toast.success('Rezervare mutată în comandă');
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Move all to order
    const handleMoveAllToOrder = async (customerId) => {
        if (!window.confirm(`Muți toate rezervările pentru ${customerId} în comandă?`)) return;
        try {
            const res = await api.post(`/reservations/v2/move-all-to-order/${customerId}`);
            playSuccessSound();
            toast.success(res.data.message);
            loadData();
        } catch (err) {
            playErrorSound();
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Helper to calculate days since first reservation item
    const getDaysSinceFirstItem = (reservation) => {
        if (!reservation.items?.length) return 0;
        const dates = reservation.items
            .map(item => item.created_at || item.added_at)
            .filter(Boolean)
            .sort();
        if (dates.length === 0) return 0;
        const firstDate = new Date(dates[0]);
        const now = new Date();
        const diffTime = now - firstDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Count customers with old reservations (> 7 days)
    const oldCustomersCount = reservations.filter(r => {
        if (isVirtualClient(r.customer_id)) return false;
        return getDaysSinceFirstItem(r) > 7;
    }).length;

    // Filter reservations
    const filteredReservations = reservations.filter(r => {
        // Filter by virtual client visibility
        if (!showVirtualClients && isVirtualClient(r.customer_id)) {
            return false;
        }
        // Filter by old customers only
        if (showOldOnly) {
            if (isVirtualClient(r.customer_id)) return false;
            if (getDaysSinceFirstItem(r) <= 7) return false;
        }
        // Filter by customer ID
        if (filterCustomer && !r.customer_id.toLowerCase().includes(filterCustomer.toLowerCase())) {
            return false;
        }
        // Filter by box search
        if (boxSearch) {
            const boxInfo = customerBoxes[r.customer_id];
            if (!boxInfo) return false;
            const boxMessage = boxInfo.box_message || '';
            const boxes = boxInfo.boxes || [];
            const boxNumbers = boxes.map(b => String(b.box_number));
            const searchLower = boxSearch.toLowerCase();
            const hasBoxMatch = boxMessage.toLowerCase().includes(searchLower) ||
                boxNumbers.some(n => n.toLowerCase().includes(searchLower));
            if (!hasBoxMatch) return false;
        }
        // Filter by search (searches in product name, barcode, customer)
        if (search) {
            const s = search.toLowerCase();
            const hasMatch = r.items?.some(item => 
                item.name?.toLowerCase().includes(s) ||
                item.barcode?.toLowerCase().includes(s) ||
                item.customer_id?.toLowerCase().includes(s)
            );
            if (!hasMatch && !r.customer_id.toLowerCase().includes(s)) return false;
        }
        return true;
    });
    
    // Sort reservations based on sortBy
    const sortedReservations = [...filteredReservations].sort((a, b) => {
        if (sortBy === 'value') {
            return (b.total_value || 0) - (a.total_value || 0);
        } else if (sortBy === 'box') {
            const boxA = customerBoxes[a.customer_id];
            const boxB = customerBoxes[b.customer_id];
            const boxNumA = boxA?.boxes?.[0]?.box_number || boxA?.box?.box_number || 'ZZZ';
            const boxNumB = boxB?.boxes?.[0]?.box_number || boxB?.box?.box_number || 'ZZZ';
            // Try numeric sort first
            const numA = parseInt(boxNumA);
            const numB = parseInt(boxNumB);
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }
            return String(boxNumA).localeCompare(String(boxNumB));
        } else if (sortBy === 'date') {
            const boxA = customerBoxes[a.customer_id];
            const boxB = customerBoxes[b.customer_id];
            const dateA = boxA?.boxes?.[0]?.assigned_at || boxA?.box?.assigned_at || '';
            const dateB = boxB?.boxes?.[0]?.assigned_at || boxB?.box?.assigned_at || '';
            return dateA.localeCompare(dateB); // Oldest first
        }
        return 0;
    });

    // Group reservations by base customer ID (123, 123-1, 123-2 all under 123)
    const groupedByBaseCustomer = sortedReservations.reduce((acc, reservation) => {
        const baseId = getBaseCustomerId(reservation.customer_id);
        if (!acc[baseId]) {
            acc[baseId] = {
                baseCustomerId: baseId,
                subCustomers: [],
                totalValue: 0,
                totalQty: 0,
                totalItems: 0,
                name: '',
                phone: '',
                address: ''
            };
        }
        acc[baseId].subCustomers.push(reservation);
        acc[baseId].totalValue += reservation.total_value || 0;
        acc[baseId].totalQty += reservation.total_qty || 0;
        acc[baseId].totalItems += reservation.items?.length || 0;
        // Use name/phone from base customer or first available
        if (!acc[baseId].name && reservation.name) acc[baseId].name = reservation.name;
        if (!acc[baseId].phone && reservation.phone) acc[baseId].phone = reservation.phone;
        if (!acc[baseId].address && reservation.address) acc[baseId].address = reservation.address;
        return acc;
    }, {});

    // Convert to array and sort by total value
    const groupedCustomers = Object.values(groupedByBaseCustomer).sort((a, b) => {
        if (sortBy === 'value') {
            return b.totalValue - a.totalValue;
        }
        return 0;
    });

    // Get unique customers for filter
    const uniqueCustomers = [...new Set(reservations.map(r => r.customer_id))].sort();

    if (loading) {
        return (
            <div className="p-4 sm:p-6 animate-pulse">
                <div className="h-8 bg-zinc-200 rounded w-48 mb-6"></div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="h-24 bg-zinc-200 rounded-xl"></div>
                    <div className="h-24 bg-zinc-200 rounded-xl"></div>
                    <div className="h-24 bg-zinc-200 rounded-xl"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-80px)] flex flex-col p-4 sm:p-6">
            {/* Fixed Header Section */}
            <div className="flex-shrink-0">
                {/* Header with Global Search */}
                <div className="flex flex-col gap-3 mb-4">
                    {/* Title and Search Row */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        <h1 className="text-lg sm:text-2xl font-bold text-zinc-800 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <BookmarkCheck size={20} className="text-teal-500 sm:w-6 sm:h-6" />
                            <span className="hidden xs:inline">Rezervări</span>
                            <span className="xs:hidden">Rez.</span>
                        </h1>
                        
                        {/* Global Search Bar - Compact */}
                        <div ref={globalSearchRef} className="relative flex-1 max-w-md">
                            <Search size={16} className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                type="text"
                                placeholder="Caută..."
                                value={globalSearchQuery}
                                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                                onFocus={() => (globalSearchResults.customers.length > 0 || globalSearchResults.products.length > 0 || globalSearchResults.boxes.length > 0) && setShowGlobalSearchResults(true)}
                                className="pl-8 sm:pl-9 pr-7 sm:pr-8 h-8 sm:h-9 text-sm"
                                data-testid="global-search-reservations"
                            />
                            {globalSearchQuery && (
                                <button
                                    onClick={() => {
                                        setGlobalSearchQuery('');
                                        setShowGlobalSearchResults(false);
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                                >
                                    <X size={16} />
                                </button>
                            )}
                            
                            {/* Global Search Results Dropdown */}
                            {showGlobalSearchResults && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                                    {searchLoading && (
                                        <div className="p-3 text-center text-zinc-500 text-sm">
                                            Se caută...
                                        </div>
                                    )}
                                    
                                    {/* Customers Section */}
                                    {globalSearchResults.customers.length > 0 && (
                                        <div className="border-b border-zinc-100">
                                            <div className="px-3 py-2 bg-zinc-50 text-xs font-semibold text-zinc-500 flex items-center gap-1">
                                                <User size={12} />
                                                Clienți ({globalSearchResults.customers.length})
                                            </div>
                                            {globalSearchResults.customers.map((customer) => (
                                                <button
                                                    key={customer.customer_id}
                                                    onClick={() => navigateToCustomer(customer.customer_id)}
                                                    className="w-full px-3 py-2 hover:bg-zinc-50 text-left flex items-center justify-between gap-2 transition-colors"
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`font-medium truncate ${customer.is_virtual ? 'text-violet-700' : 'text-zinc-800'}`}>
                                                                {customer.customer_name || customer.customer_id}
                                                            </span>
                                                            {customer.is_virtual && (
                                                                <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded">VIRTUAL</span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-zinc-500 flex items-center gap-2">
                                                            <span>{customer.customer_id}</span>
                                                            {customer.phone && <span>• {customer.phone}</span>}
                                                            {customer.box_number && <span>• Cutia {customer.box_number}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="text-sm font-semibold text-teal-600">{formatPrice(customer.total_value)}</div>
                                                        <div className="text-[10px] text-zinc-400">{customer.total_items} prod.</div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Products Section (FIFO) */}
                                    {globalSearchResults.products.length > 0 && (
                                        <div className="border-b border-zinc-100">
                                            <div className="px-3 py-2 bg-zinc-50 text-xs font-semibold text-zinc-500 flex items-center gap-1">
                                                <Package size={12} />
                                                Produse (Stoc FIFO)
                                            </div>
                                            {globalSearchResults.products.map((product) => (
                                                <div key={product.barcode} className="px-3 py-2">
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-zinc-800 truncate">{product.name}</p>
                                                            <p className="text-xs text-zinc-500 font-mono">{product.barcode}</p>
                                                        </div>
                                                        <div className="text-right shrink-0 text-xs">
                                                            {product.pret_mediu_min > 0 && (
                                                                <p className="text-blue-600">Ach: {formatPrice(product.pret_mediu_min)}</p>
                                                            )}
                                                            {product.pret_vanzare_min > 0 && (
                                                                <p className="text-green-600">Vânz: {formatPrice(product.pret_vanzare_min)}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Stock locations */}
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {product.store_qty > 0 && (
                                                            <span className="px-2 py-1 text-xs bg-teal-100 text-teal-700 rounded flex items-center gap-1">
                                                                <ShoppingCart size={12} />
                                                                Magazin: {product.store_qty} buc
                                                                {product.store_entry && (
                                                                    <span className="text-teal-500 ml-1">
                                                                        ({new Date(product.store_entry).toLocaleDateString('ro-RO')})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}
                                                        {product.pallet_locations.slice(0, 3).map((loc, idx) => (
                                                            <span 
                                                                key={idx}
                                                                className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded flex items-center gap-1"
                                                            >
                                                                <Package size={12} />
                                                                Palet {loc.pallet_number}: {loc.quantity} buc
                                                                {loc.entry_timestamp && (
                                                                    <span className="text-amber-500 ml-1">
                                                                        ({new Date(loc.entry_timestamp).toLocaleDateString('ro-RO')})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        ))}
                                                        {product.pallet_locations.length > 3 && (
                                                            <span className="px-2 py-1 text-xs bg-zinc-100 text-zinc-600 rounded">
                                                                +{product.pallet_locations.length - 3} locații
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Reserved in other orders */}
                                                    {product.reserved_in.length > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-zinc-100">
                                                            <p className="text-xs text-red-600 font-medium mb-1">
                                                                <AlertCircle size={12} className="inline mr-1" />
                                                                Rezervat în alte comenzi:
                                                            </p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {product.reserved_in.slice(0, 3).map((res, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => navigateToCustomer(res.customer_id)}
                                                                        className="px-2 py-0.5 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                                                                    >
                                                                        {res.customer_name || res.customer_id}: {res.quantity} buc
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Boxes Section */}
                                    {globalSearchResults.boxes.length > 0 && (
                                        <div>
                                            <div className="px-3 py-2 bg-zinc-50 text-xs font-semibold text-zinc-500 flex items-center gap-1">
                                                <Box size={12} />
                                                Cutii ({globalSearchResults.boxes.length})
                                            </div>
                                            {globalSearchResults.boxes.map((box) => (
                                                <button
                                                    key={box.customer_id}
                                                    onClick={() => navigateToCustomer(box.customer_id)}
                                                    className="w-full px-3 py-2 hover:bg-zinc-50 text-left flex items-center justify-between gap-2 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-bold">
                                                            {box.box_number}
                                                        </span>
                                                        <span className="text-zinc-700">{box.customer_name || box.customer_id}</span>
                                                    </div>
                                                    <span className="text-xs text-zinc-500">{box.total_items} prod.</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* No results */}
                                    {!searchLoading && globalSearchResults.customers.length === 0 && 
                                     globalSearchResults.products.length === 0 && 
                                     globalSearchResults.boxes.length === 0 && globalSearchQuery && (
                                        <div className="p-4 text-center text-zinc-500 text-sm">
                                            Niciun rezultat pentru "{globalSearchQuery}"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Action Buttons - Full width on mobile, prominent */}
                    <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-2 sm:flex-wrap">
                        <Button
                            onClick={openStoreScanner}
                            className="bg-teal-600 hover:bg-teal-700 h-12 sm:h-9 text-sm sm:text-sm font-medium"
                            data-testid="btn-din-magazin"
                        >
                            <ScanLine size={18} className="mr-1 sm:mr-1.5 sm:w-4 sm:h-4" />
                            Magazin
                        </Button>
                        <Button
                            onClick={openPalletScanner}
                            className={`h-12 sm:h-9 text-sm sm:text-sm font-medium ${activePallet ? "bg-amber-600 hover:bg-amber-700" : "bg-zinc-400 cursor-not-allowed"}`}
                            disabled={!activePallet}
                            title={activePallet ? `Palet: ${activePallet.id}` : 'Nu există palet deschis'}
                            data-testid="btn-din-palet"
                        >
                            <Package size={18} className="mr-1 sm:mr-1.5 sm:w-4 sm:h-4" />
                            Palet
                        </Button>
                        <Button
                            onClick={handleConsolidateDuplicates}
                            variant="outline"
                            className="border-violet-300 text-violet-700 hover:bg-violet-50 h-12 sm:h-9 text-sm sm:text-sm font-medium"
                            title="Combină produsele duplicate din aceeași rezervare"
                        >
                            <Layers size={18} className="mr-1 sm:mr-1.5 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Consolidează</span>
                            <span className="sm:hidden">Unește</span>
                        </Button>
                    </div>
                </div>

                {/* Summary Cards - scrollable on mobile */}
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-4">
                    <div className="grid grid-cols-4 gap-2 sm:gap-3 min-w-[500px] sm:min-w-0">
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-2 sm:p-4 text-center">
                            <div className="text-lg sm:text-2xl font-bold text-teal-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {summary.total_qty}
                            </div>
                            <p className="text-[10px] sm:text-xs text-teal-500">Produse</p>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2 sm:p-4 text-center">
                            <div className="text-lg sm:text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(
                                    reservations
                                        .filter(r => !isVirtualClient(r.customer_id))
                                        .reduce((sum, r) => sum + (r.total_value || 0), 0)
                                )}
                            </div>
                            <p className="text-[10px] sm:text-xs text-emerald-500">Reali</p>
                        </div>
                        <div className="bg-violet-50 border border-violet-200 rounded-xl p-2 sm:p-4 text-center">
                            <div className="text-lg sm:text-2xl font-bold text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatPrice(
                                    reservations
                                        .filter(r => isVirtualClient(r.customer_id))
                                        .reduce((sum, r) => sum + (r.total_value || 0), 0)
                                )}
                            </div>
                            <p className="text-[10px] sm:text-xs text-violet-500">Virtuali</p>
                        </div>
                        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-2 sm:p-4 text-center">
                            <div className="text-lg sm:text-2xl font-bold text-zinc-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {reservations.filter(r => !isVirtualClient(r.customer_id)).length}/{reservations.filter(r => isVirtualClient(r.customer_id)).length}
                            </div>
                            <p className="text-[10px] sm:text-xs text-zinc-500">R/V</p>
                        </div>
                    </div>
                </div>

                {/* Search Bars - Stacked on mobile */}
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    {/* Client Search - Primary */}
                    <div className="relative flex-1">
                        <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            placeholder="Caută client..."
                            value={filterCustomer}
                            onChange={(e) => setFilterCustomer(e.target.value)}
                            className="pl-10 h-9"
                            data-testid="search-client"
                        />
                    </div>
                    <div className="flex gap-2">
                        {/* Box Search */}
                        <div className="relative w-24 sm:w-32">
                            <Box size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-400" />
                            <Input
                                placeholder="Cutia"
                                value={boxSearch}
                                onChange={(e) => setBoxSearch(e.target.value)}
                                className="pl-8 h-9 border-blue-200 focus:border-blue-400"
                                data-testid="search-box"
                            />
                        </div>
                        {/* Product Search */}
                        <div className="relative flex-1 min-w-[100px]">
                            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                            <Input
                                placeholder="Produs..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-8 h-9"
                                data-testid="search-reservations"
                            />
                        </div>
                        {/* Virtual Clients Toggle */}
                        <button
                            onClick={() => setShowVirtualClients(!showVirtualClients)}
                            className={`px-2 sm:px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium flex items-center gap-1 transition-colors whitespace-nowrap ${
                                showVirtualClients 
                                    ? 'bg-violet-100 border-violet-300 text-violet-700' 
                                    : 'bg-zinc-100 border-zinc-200 text-zinc-500 hover:bg-zinc-200'
                            }`}
                            title={showVirtualClients ? 'Ascunde clienții virtuali' : 'Arată clienții virtuali'}
                            data-testid="toggle-virtual-clients"
                        >
                            <Users size={14} />
                            <span className="hidden sm:inline">{showVirtualClients ? 'Virtuali ON' : 'Virtuali OFF'}</span>
                        </button>
                    </div>
                </div>
                
                {/* Sort buttons - scrollable on mobile */}
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-3">
                    <div className="flex gap-1.5 sm:gap-2 items-center min-w-max">
                        <span className="text-[10px] sm:text-xs text-zinc-500">Sort:</span>
                        <button
                            onClick={() => setSortBy('value')}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors ${
                                sortBy === 'value' 
                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' 
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                        >
                            💰 Sumă
                        </button>
                        <button
                            onClick={() => setSortBy('box')}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors ${
                                sortBy === 'box' 
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                        >
                            📦 Cutie
                        </button>
                        <button
                            onClick={() => setSortBy('date')}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors ${
                                sortBy === 'date' 
                                    ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                        >
                            📅 Data
                        </button>
                        
                        <span className="text-zinc-300 mx-0.5 sm:mx-1">|</span>
                        
                        {/* Old customers filter */}
                        <button
                            onClick={() => setShowOldOnly(!showOldOnly)}
                            className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors flex items-center gap-1 ${
                                showOldOnly 
                                    ? 'bg-red-100 text-red-700 border border-red-300' 
                                    : oldCustomersCount > 0 
                                        ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                            }`}
                        >
                            ⏰ +7zi
                            {oldCustomersCount > 0 && (
                                <span className={`px-1 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold ${
                                    showOldOnly ? 'bg-red-200 text-red-800' : 'bg-red-500 text-white'
                                }`}>
                                    {oldCustomersCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

            </div>
            {/* End Fixed Header Section */}

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto pb-24">
                {/* Reservations List */}
                {sortedReservations.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                    <BookmarkCheck size={48} className="mx-auto mb-4 text-zinc-300" />
                    <p>Nu există rezervări</p>
                    <div className="flex gap-3 justify-center mt-4">
                        <Button
                            onClick={openStoreScanner}
                            variant="outline"
                            className="border-teal-300 text-teal-700 hover:bg-teal-50"
                        >
                            <ScanLine size={16} className="mr-2" />
                            Din Magazin
                        </Button>
                        <Button
                            onClick={openPalletScanner}
                            variant="outline"
                            className={activePallet ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "opacity-50 cursor-not-allowed"}
                            disabled={!activePallet}
                        >
                            <Package size={16} className="mr-2" />
                            Din Palet
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {groupedCustomers.map(group => {
                        const hasMultipleBoxes = group.subCustomers.length > 1;
                        const baseIsVirtual = isVirtualClient(group.baseCustomerId);
                        
                        // If only one sub-customer, render it directly (old behavior)
                        if (!hasMultipleBoxes) {
                            const reservation = group.subCustomers[0];
                            // Determine box status for coloring
                            const boxInfo = customerBoxes[reservation.customer_id];
                            const isVirtual = isVirtualClient(reservation.customer_id);
                            const hasBox = boxInfo?.has_box;
                            const needsBox = !isVirtual && !hasBox;
                        
                        // Background color based on status
                        // PHONE-BASED COLORING:
                        // - Red: no phone (highest priority)
                        // - Blue: phone only (no name/address)
                        // - Green: phone + name/address (complete)
                        // Other statuses override phone coloring
                        const hasPhone = reservation.phone && reservation.phone.trim();
                        const hasName = reservation.name && reservation.name.trim();
                        const hasAddress = reservation.address && reservation.address.trim();
                        
                        let cardBgClass = 'bg-white border-zinc-200';
                        let headerBgClass = 'bg-zinc-50 hover:bg-zinc-100';
                        
                        if (checkedCustomers[reservation.customer_id]) {
                            cardBgClass = 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-200';
                            headerBgClass = 'bg-emerald-100 hover:bg-emerald-150';
                        } else if (isVirtual) {
                            cardBgClass = 'bg-violet-50 border-violet-300';
                            headerBgClass = 'bg-violet-50 hover:bg-violet-100';
                        } else if (!hasPhone) {
                            // No phone - RED (highest priority for real clients)
                            cardBgClass = 'bg-red-50 border-red-300';
                            headerBgClass = 'bg-red-50 hover:bg-red-100';
                        } else if (hasPhone && !hasName && !hasAddress) {
                            // Phone only - BLUE
                            cardBgClass = 'bg-blue-50 border-blue-300';
                            headerBgClass = 'bg-blue-50 hover:bg-blue-100';
                        } else if (hasPhone && (hasName || hasAddress)) {
                            // Complete client - GREEN
                            cardBgClass = 'bg-green-50 border-green-300';
                            headerBgClass = 'bg-green-50 hover:bg-green-100';
                        }
                        
                        return (
                        <div 
                            key={reservation.customer_id} 
                            className={`border rounded-xl overflow-hidden ${cardBgClass}`}
                            data-customer-id={reservation.customer_id}
                        >
                            {/* Customer Header - Clickable to expand */}
                            <div 
                                className={`px-3 sm:px-4 py-2 sm:py-3 cursor-pointer transition-colors ${headerBgClass}`}
                                onClick={() => toggleCustomer(reservation.customer_id)}
                            >
                                {/* Mobile: Stack layout, Desktop: Inline */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        {/* Check button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCustomerCheck(reservation.customer_id);
                                            }}
                                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
                                                checkedCustomers[reservation.customer_id]
                                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                                    : 'border-zinc-300 hover:border-emerald-400 bg-white'
                                            }`}
                                            title={checkedCustomers[reservation.customer_id] ? 'Debifează' : 'Bifează'}
                                            data-testid={`btn-check-${reservation.customer_id}`}
                                        >
                                            {checkedCustomers[reservation.customer_id] && <Check size={14} strokeWidth={3} />}
                                        </button>
                                        
                                        {expandedCustomers[reservation.customer_id] ? (
                                            <ChevronDown size={18} className="text-zinc-500 shrink-0" />
                                        ) : (
                                            <ChevronRight size={18} className="text-zinc-500 shrink-0" />
                                        )}
                                        
                                        {/* Client ID and name */}
                                        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                            {isVirtualClient(reservation.customer_id) && (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-medium hidden sm:flex items-center gap-1">
                                                    <User size={10} />
                                                    Virtual
                                                </span>
                                            )}
                                            <span className={`font-bold ${checkedCustomers[reservation.customer_id] ? 'text-emerald-700' : isVirtualClient(reservation.customer_id) ? 'text-violet-700' : 'text-zinc-800'}`}>
                                                {reservation.customer_id}
                                            </span>
                                            {/* Customer Name */}
                                            {reservation.customer_name ? (
                                                <span className="text-sm text-zinc-600 font-medium">
                                                    - {reservation.customer_name}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-zinc-400 italic hidden sm:inline">
                                                    (Client necunoscut)
                                                </span>
                                            )}
                                            {reservation.phone ? (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium hidden sm:flex items-center gap-1">
                                                    <Phone size={10} />
                                                    {reservation.phone}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium flex items-center gap-1">
                                                    Fără tel
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Right side: Summary */}
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs sm:text-sm text-zinc-600">
                                            {reservation.items?.length || 0} prod • <span className="font-semibold text-zinc-800">{formatPrice(reservation.total_value)}</span>
                                        </span>
                                        {(() => {
                                            const days = getDaysSinceFirstItem(reservation);
                                            if (days <= 0) return null;
                                            const isOld = days > 7;
                                            return (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                    isOld 
                                                        ? 'bg-red-100 text-red-700' 
                                                        : 'bg-zinc-100 text-zinc-600'
                                                }`}>
                                                    {days}z
                                                </span>
                                            );
                                        })()}
                                        {/* Box indicator - Interactive with edit, date, and add extra */}
                                        {(() => {
                                            const boxInfo = customerBoxes[reservation.customer_id];
                                            if (!boxInfo) return null;
                                            
                                            if (boxInfo.is_virtual) {
                                                // Virtual client - show box if assigned, otherwise show assign button
                                                const hasBox = boxInfo.has_box && boxInfo.box_number;
                                                return (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openVirtualBoxModal(reservation.customer_id, boxInfo.box_number || '');
                                                        }}
                                                        className={`text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1 transition-colors ${
                                                            hasBox
                                                                ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                                                                : 'bg-red-100 text-red-700 hover:bg-red-200 animate-pulse'
                                                        }`}
                                                        title="Click pentru a edita cutia"
                                                        data-testid={`virtual-box-btn-${reservation.customer_id}`}
                                                    >
                                                        <Box size={10} />
                                                        {hasBox ? `CUTIA ${boxInfo.box_number}` : 'ATRIBUIE CUTIE'}
                                                    </button>
                                                );
                                            }
                                            
                                            const boxMessage = boxInfo.box_message;
                                            const hasBox = boxInfo.has_box;
                                            const isNoBox = boxInfo.box?.box_number === 'F';
                                            
                                            // Get the earliest box assignment date
                                            let boxDate = null;
                                            if (boxInfo.boxes?.length > 0) {
                                                const dates = boxInfo.boxes.map(b => b.assigned_at).filter(Boolean).sort();
                                                if (dates.length > 0) {
                                                    boxDate = new Date(dates[0]);
                                                }
                                            } else if (boxInfo.box?.assigned_at) {
                                                boxDate = new Date(boxInfo.box.assigned_at);
                                            }
                                            
                                            const formatBoxDate = (date) => {
                                                if (!date) return '';
                                                const day = date.getDate().toString().padStart(2, '0');
                                                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                                return `${day}.${month}`;
                                            };
                                            
                                            return (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditBoxModal(reservation.customer_id);
                                                        }}
                                                        className={`text-[10px] px-2 py-0.5 rounded font-medium flex items-center gap-1 transition-colors ${
                                                            isNoBox
                                                                ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                                                : hasBox
                                                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                                    : 'bg-red-100 text-red-700 hover:bg-red-200 animate-pulse'
                                                        }`}
                                                        title="Click pentru a edita cutia"
                                                    >
                                                        <Box size={10} />
                                                        {boxMessage || 'ATRIBUIE CUTIE'}
                                                        {boxDate && (
                                                            <span className="text-[9px] opacity-75 ml-1">
                                                                ({formatBoxDate(boxDate)})
                                                            </span>
                                                        )}
                                                    </button>
                                                    {hasBox && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openAddExtraBoxModal(reservation.customer_id);
                                                            }}
                                                            className="w-5 h-5 rounded bg-green-100 text-green-600 hover:bg-green-200 flex items-center justify-center transition-colors"
                                                            title="Adaugă cutie suplimentară"
                                                        >
                                                            <Plus size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Action Buttons - Always visible, prominent on mobile */}
                            {expandedCustomers[reservation.customer_id] && (
                                <div className="px-3 sm:px-4 py-2 bg-white/80 border-t border-b border-zinc-100" onClick={(e) => e.stopPropagation()}>
                                    <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCopyReservation(reservation)}
                                            className="border-zinc-300 text-zinc-600 hover:bg-zinc-100 h-9 sm:h-8 text-xs sm:text-sm"
                                            title="Copiază lista"
                                        >
                                            <Copy size={14} className="mr-1" />
                                            Copiază
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openAddModalForCustomer(reservation.customer_id)}
                                            className="border-teal-300 text-teal-600 hover:bg-teal-50 h-9 sm:h-8 text-xs sm:text-sm"
                                            data-testid={`btn-add-${reservation.customer_id}`}
                                        >
                                            <Plus size={14} className="mr-1" />
                                            Adaugă
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openMoveAllModal(reservation.customer_id, reservation)}
                                            className="border-blue-300 text-blue-600 hover:bg-blue-50 h-9 sm:h-8 text-xs sm:text-sm"
                                            data-testid={`btn-move-all-${reservation.customer_id}`}
                                        >
                                            <ArrowRightLeft size={14} className="mr-1" />
                                            Mută
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleDeleteAllReservations(reservation.customer_id)}
                                            className="border-red-300 text-red-600 hover:bg-red-50 h-9 sm:h-8 text-xs sm:text-sm"
                                            data-testid={`btn-delete-all-${reservation.customer_id}`}
                                        >
                                            <XCircle size={14} className="mr-1" />
                                            Șterge
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => { e.stopPropagation(); openEditClientModal(reservation); }}
                                            className="border-violet-300 text-violet-600 hover:bg-violet-50 h-9 sm:h-8 text-xs sm:text-sm"
                                        >
                                            <Edit2 size={14} className="mr-1" />
                                            Date
                                        </Button>
                                        {/* Main action button - full width on mobile */}
                                        {isVirtualClient(reservation.customer_id) ? (
                                            <Button
                                                size="sm"
                                                onClick={() => handleCompleteVirtualReservation(reservation.customer_id)}
                                                className="bg-violet-500 hover:bg-violet-600 h-9 sm:h-8 text-xs sm:text-sm"
                                            >
                                                <Check size={14} className="mr-1" />
                                                Finalizează
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                onClick={() => handleMoveAllToOrder(reservation.customer_id)}
                                                className="bg-emerald-600 hover:bg-emerald-700 h-9 sm:h-8 text-xs sm:text-sm"
                                            >
                                                <ArrowRight size={14} className="mr-1" />
                                                Comandă
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Items Table - Expandable */}
                            {expandedCustomers[reservation.customer_id] && (
                                <div className="divide-y divide-zinc-100">
                                    {reservation.items?.map(item => {
                                        const isGift = item.is_gift || item.source === 'gift';
                                        return (
                                        <div key={item.id} className={`px-4 py-2 flex items-center justify-between ${isGift ? 'bg-pink-50 hover:bg-pink-100' : 'hover:bg-zinc-50'}`}>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    {isGift && <span className="text-pink-500">🎁</span>}
                                                    <p className={`font-medium ${isGift ? 'text-pink-700' : 'text-zinc-800'}`}>
                                                        {isGift && <span className="font-bold text-pink-600 mr-1">CADOU:</span>}
                                                        {item.name}
                                                    </p>
                                                    {item.source === 'pallet' && !isGift && (
                                                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                                                            din Palet
                                                        </span>
                                                    )}
                                                    {item.source === 'virtual' && !isGift && (
                                                        <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">
                                                            Virtual
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-zinc-500">
                                                    {isGift ? (
                                                        <span className="text-pink-500 font-medium">Gratuit ({item.quantity} buc)</span>
                                                    ) : (
                                                        <>Cod: {item.barcode} • {item.quantity} x {formatPrice(item.unit_price)} RON</>
                                                    )}
                                                    {item.created_at && (
                                                        <span className="ml-2 text-zinc-400">
                                                            • {new Date(item.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {isGift ? (
                                                    <span className="font-bold text-sm text-pink-500 mr-2">GRATUIT</span>
                                                ) : (
                                                    <span className="font-bold text-teal-600 mr-2">
                                                        {formatPrice(item.quantity * item.unit_price)} RON
                                                    </span>
                                                )}
                                                {!isGift && (
                                                    <button
                                                        onClick={() => {
                                                            setEditingItem(item);
                                                            setEditQty(item.quantity.toString());
                                                            setEditPrice(item.unit_price.toString());
                                                            setShowEditModal(true);
                                                        }}
                                                        className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded"
                                                        title="Editează"
                                                        data-testid={`btn-edit-${item.id}`}
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                )}
                                                {!isGift && (
                                                    <button
                                                        onClick={() => openMoveModal(item)}
                                                        className="p-1.5 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                        title={isVirtualClient(item.customer_id) ? "Mută la alt client virtual" : "Mută la alt client"}
                                                        data-testid={`btn-move-client-${item.id}`}
                                                    >
                                                        <ArrowRightLeft size={14} />
                                                    </button>
                                                )}
                                                {/* Only show "Mută în comandă" for NORMAL clients and non-gifts */}
                                                {!isVirtualClient(item.customer_id) && !isGift && (
                                                    <button
                                                        onClick={() => handleMoveToOrder(item.id)}
                                                        className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                                                        title="Mută în comandă"
                                                        data-testid={`btn-move-order-${item.id}`}
                                                    >
                                                        <ArrowRight size={14} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteReservation(item.id)}
                                                    className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                    title={isGift ? "Șterge cadoul" : isVirtualClient(item.customer_id) ? "Șterge (fără returnare stoc)" : "Șterge și returnează în magazin"}
                                                    data-testid={`btn-delete-${item.id}`}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        );
                        }
                        
                        // Multiple boxes - show group header with total, then individual boxes
                        return (
                            <div key={group.baseCustomerId} className="border-2 border-blue-300 rounded-xl overflow-hidden bg-blue-50">
                                {/* Group Header - Total for all boxes */}
                                <div 
                                    className="px-4 py-3 bg-blue-100 hover:bg-blue-200 cursor-pointer flex items-center justify-between transition-colors"
                                    onClick={() => toggleGroup(group.baseCustomerId)}
                                >
                                    <div className="flex items-center gap-3">
                                        {expandedGroups[group.baseCustomerId] ? (
                                            <ChevronDown size={20} className="text-blue-600" />
                                        ) : (
                                            <ChevronRight size={20} className="text-blue-600" />
                                        )}
                                        <Users size={18} className="text-blue-600" />
                                        <span className="font-bold text-blue-800 text-lg">{group.baseCustomerId}</span>
                                        {group.name && (
                                            <span className="text-blue-700 font-medium">- {group.name}</span>
                                        )}
                                        {group.phone && (
                                            <span className="text-[11px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium flex items-center gap-1">
                                                <Phone size={10} />
                                                {group.phone}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-blue-600">
                                            {group.subCustomers.length} cutii • {group.totalItems} produse
                                        </span>
                                        <span className="font-bold text-lg text-blue-800">
                                            TOTAL: {formatPrice(group.totalValue)} RON
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Individual boxes */}
                                {expandedGroups[group.baseCustomerId] && (
                                    <div className="p-3 space-y-3">
                                        {group.subCustomers.map((reservation, subIdx) => {
                                            const boxInfo = customerBoxes[reservation.customer_id];
                                            const boxNumber = boxInfo?.boxes?.[0]?.box_number || boxInfo?.box?.box_number;
                                            const isVirtual = isVirtualClient(reservation.customer_id);
                                            const hasBox = boxInfo?.has_box;
                                            
                                            // Phone-based coloring for sub-cards
                                            const hasPhoneSub = reservation.phone && reservation.phone.trim();
                                            const hasNameSub = reservation.name && reservation.name.trim();
                                            const hasAddressSub = reservation.address && reservation.address.trim();
                                            
                                            let subCardClass = 'bg-white border-zinc-200';
                                            if (checkedCustomers[reservation.customer_id]) {
                                                subCardClass = 'bg-emerald-50 border-emerald-400';
                                            } else if (isVirtual) {
                                                subCardClass = 'bg-violet-50 border-violet-300';
                                            } else if (!hasPhoneSub) {
                                                // No phone - RED
                                                subCardClass = 'bg-red-50 border-red-300';
                                            } else if (hasPhoneSub && !hasNameSub && !hasAddressSub) {
                                                // Phone only - BLUE
                                                subCardClass = 'bg-blue-50 border-blue-300';
                                            } else if (hasPhoneSub && (hasNameSub || hasAddressSub)) {
                                                // Complete - GREEN
                                                subCardClass = 'bg-green-50 border-green-300';
                                            }
                                            
                                            return (
                                                <div 
                                                    key={reservation.customer_id}
                                                    className={`border rounded-lg overflow-hidden ${subCardClass}`}
                                                >
                                                    <div 
                                                        className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-zinc-50"
                                                        onClick={() => toggleCustomer(reservation.customer_id)}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            {/* Check button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleCustomerCheck(reservation.customer_id);
                                                                }}
                                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                                    checkedCustomers[reservation.customer_id]
                                                                        ? 'bg-emerald-500 border-emerald-500 text-white'
                                                                        : 'border-zinc-300 hover:border-emerald-400 bg-white'
                                                                }`}
                                                            >
                                                                {checkedCustomers[reservation.customer_id] && <Check size={12} strokeWidth={3} />}
                                                            </button>
                                                            
                                                            {expandedCustomers[reservation.customer_id] ? (
                                                                <ChevronDown size={16} className="text-zinc-400" />
                                                            ) : (
                                                                <ChevronRight size={16} className="text-zinc-400" />
                                                            )}
                                                            
                                                            {boxNumber && (
                                                                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-bold">
                                                                    CUTIA {boxNumber}
                                                                </span>
                                                            )}
                                                            
                                                            <span className="font-medium text-zinc-700">
                                                                {hasSuffix(reservation.customer_id) ? reservation.customer_id : 'Principal'}
                                                            </span>
                                                            
                                                            <span className="text-sm text-zinc-500">
                                                                {reservation.items?.length || 0} produse
                                                            </span>
                                                        </div>
                                                        
                                                        {/* Action buttons for sub-client */}
                                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleCopyReservation(reservation)}
                                                                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100 h-7 px-2 text-xs"
                                                                title="Copiază lista"
                                                            >
                                                                <Copy size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => openAddModalForCustomer(reservation.customer_id)}
                                                                className="border-teal-300 text-teal-600 hover:bg-teal-50 h-7 px-2 text-xs"
                                                                title="Adaugă produs"
                                                            >
                                                                <Plus size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => openMoveAllModal(reservation.customer_id, reservation)}
                                                                className="border-blue-300 text-blue-600 hover:bg-blue-50 h-7 px-2 text-xs"
                                                                title="Mută la alt client"
                                                            >
                                                                <ArrowRightLeft size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleDeleteAllReservations(reservation.customer_id)}
                                                                className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                                                                title="Șterge toate"
                                                            >
                                                                <XCircle size={12} />
                                                            </Button>
                                                            {isVirtual ? (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleCompleteVirtualReservation(reservation.customer_id)}
                                                                    className="bg-violet-500 hover:bg-violet-600 h-7 px-2 text-xs"
                                                                    title="Finalizează"
                                                                >
                                                                    <Check size={12} />
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => handleMoveAllToOrder(reservation.customer_id)}
                                                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"
                                                                    title="Mută în comandă"
                                                                >
                                                                    <ArrowRight size={12} />
                                                                </Button>
                                                            )}
                                                            <span className="font-bold text-teal-600 ml-2">
                                                                {formatPrice(reservation.total_value)} RON
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Items list when expanded */}
                                                    {expandedCustomers[reservation.customer_id] && (
                                                        <div className="px-3 pb-3 border-t border-zinc-100">
                                                            <div className="space-y-1 mt-2">
                                                                {(reservation.items || []).map((item, itemIdx) => {
                                                                    const isGift = item.is_gift || item.source === 'gift';
                                                                    return (
                                                                        <div key={item.id || itemIdx} className="flex items-center justify-between py-1.5 px-2 bg-zinc-50 rounded text-sm">
                                                                            <div className="flex items-center gap-2">
                                                                                {isGift && (
                                                                                    <span className="text-[9px] px-1 py-0.5 bg-pink-100 text-pink-600 rounded font-bold">CADOU</span>
                                                                                )}
                                                                                <span className="text-zinc-800">{item.name}</span>
                                                                                <span className="text-zinc-400">x{item.quantity}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                {isGift ? (
                                                                                    <span className="text-pink-500 font-medium">GRATUIT</span>
                                                                                ) : (
                                                                                    <span className="text-teal-600 font-medium">
                                                                                        {formatPrice(item.quantity * item.unit_price)} RON
                                                                                    </span>
                                                                                )}
                                                                                <button
                                                                                    onClick={() => handleDeleteReservation(item.id)}
                                                                                    className="p-1 text-zinc-400 hover:text-red-500"
                                                                                    title="Șterge"
                                                                                >
                                                                                    <Trash2 size={12} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </div>
            {/* End Scrollable Content Section */}

            {/* Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleScannedBarcode}
            />

            {/* Add Reservation Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="flex items-center gap-2">
                            {reservationSource === 'pallet' ? (
                                <>
                                    <Package size={20} className="text-amber-500" />
                                    Rezervare din Palet
                                </>
                            ) : (
                                <>
                                    <ScanLine size={20} className="text-teal-500" />
                                    Rezervare din Magazin
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Source indicator */}
                    <div className={`text-xs px-2 py-1 rounded text-center ${
                        reservationSource === 'pallet' 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-teal-100 text-teal-700'
                    }`}>
                        {reservationSource === 'pallet' 
                            ? `Produsul va fi adăugat pe palet și rezervat (nu intră în magazin)`
                            : 'Produsul va fi scăzut din stocul magazinului'}
                    </div>
                    
                    <div className="space-y-4 mt-4">
                        <div className="relative">
                            <Label>ID Client</Label>
                            <Input
                                id="reservation-customer-id"
                                value={customerId}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setCustomerId(value);
                                    getCustomerSuggestions(value);
                                }}
                                onBlur={() => {
                                    // Delay hiding to allow click on suggestion
                                    setTimeout(() => setShowCustomerSuggestions(false), 200);
                                }}
                                onFocus={() => {
                                    if (customerId) getCustomerSuggestions(customerId);
                                }}
                                placeholder="Ex: DENISA, 123"
                                className="mt-1"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        // If suggestions are showing, select the first one
                                        if (showCustomerSuggestions && customerSuggestions.length > 0) {
                                            selectCustomerSuggestion(customerSuggestions[0]);
                                            return;
                                        }
                                        setShowCustomerSuggestions(false);
                                        // If product exists (found), skip name and go to price
                                        // Otherwise go to name field (or barcode if empty)
                                        if (!productBarcode.trim()) {
                                            document.getElementById('reservation-barcode')?.focus();
                                        } else if (productFound) {
                                            // Product exists - skip name, go to price
                                            document.getElementById('reservation-price')?.focus();
                                        } else {
                                            // New product - go to name
                                            document.getElementById('reservation-product-name')?.focus();
                                        }
                                    } else if (e.key === 'ArrowDown' && customerSuggestions.length > 0) {
                                        e.preventDefault();
                                        selectCustomerSuggestion(customerSuggestions[0]);
                                    } else if (e.key === 'Escape') {
                                        setShowCustomerSuggestions(false);
                                    }
                                }}
                            />
                            {/* Customer suggestions dropdown */}
                            {showCustomerSuggestions && customerSuggestions.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                    {customerSuggestions.map((suggestion, idx) => (
                                        <button
                                            key={suggestion}
                                            type="button"
                                            className="w-full px-3 py-2 text-left hover:bg-violet-50 flex items-center gap-2 text-sm"
                                            onClick={() => selectCustomerSuggestion(suggestion)}
                                        >
                                            <User size={14} className="text-violet-500" />
                                            <span className="font-medium">{suggestion}</span>
                                            <span className="text-xs text-zinc-400 ml-auto">Client virtual</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Cod Produs</Label>
                            {productFound ? (
                                <div className="mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-md text-zinc-700 font-mono">
                                    {productBarcode}
                                </div>
                            ) : (
                                <Input
                                    id="reservation-barcode"
                                    value={productBarcode}
                                    onChange={async (e) => {
                                        // Normalize barcode: remove spaces, max 16 chars, uppercase
                                        const barcode = normalizeBarcode(e.target.value);
                                        setProductBarcode(barcode);
                                        
                                        // Check store stock and fetch last price when barcode changes
                                        if (barcode.length >= 3) {
                                            // Try to lookup product
                                            try {
                                                const lookupRes = await api.get(`/products/lookup/${barcode}`);
                                                if (lookupRes.data?.found && lookupRes.data?.product) {
                                                    const product = lookupRes.data.product;
                                                    setProductBarcode(product.barcode || barcode);
                                                    setProductName(product.name || '');
                                                    setProductCategory(product.category || '');
                                                    setProductFound(true);
                                                    // Focus on price field
                                                    setTimeout(() => {
                                                        document.getElementById('reservation-price')?.focus();
                                                    }, 50);
                                                }
                                            } catch (e) {
                                                // Product not found - keep as new (expected behavior)
                                                console.debug('Product lookup failed:', e.message);
                                            }
                                            
                                            // Check store stock (for pallet source)
                                            if (reservationSource === 'pallet') {
                                                try {
                                                    const storeRes = await api.get('/store/v2');
                                                    const storeItem = (storeRes.data || []).find(s => s.barcode?.toUpperCase() === barcode);
                                                    setStoreStock(storeItem?.quantity || 0);
                                                } catch (e) {
                                                    setStoreStock(null);
                                                }
                                            }
                                            
                                            // Fetch last used price
                                            try {
                                                const priceRes = await api.get(`/products/last-price/${barcode}`);
                                                const lastPrice = priceRes.data?.last_price || 0;
                                                setPrice(lastPrice.toString());
                                            } catch (e) {
                                                // Keep existing price if error (expected for new products)
                                                console.debug('Price lookup failed:', e.message);
                                            }
                                        }
                                    }}
                                    onKeyDown={async (e) => {
                                        // On Enter, try to lookup and move to next field
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            // Barcode is already normalized from onChange
                                            const barcode = productBarcode.trim();
                                            if (barcode.length >= 3) {
                                                try {
                                                    const lookupRes = await api.get(`/products/lookup/${barcode}`);
                                                    if (lookupRes.data?.found && lookupRes.data?.product) {
                                                        const product = lookupRes.data.product;
                                                        setProductBarcode(product.barcode || barcode);
                                                        setProductName(product.name || '');
                                                        setProductCategory(product.category || '');
                                                        setProductFound(true);
                                                        // Move to price
                                                        document.getElementById('reservation-price')?.focus();
                                                    } else {
                                                        // Product not found - move to name field
                                                        document.getElementById('reservation-product-name')?.focus();
                                                    }
                                                } catch (err) {
                                                    document.getElementById('reservation-product-name')?.focus();
                                                }
                                            } else {
                                                document.getElementById('reservation-product-name')?.focus();
                                            }
                                        }
                                    }}
                                    placeholder="Cod de bare"
                                    className="mt-1"
                                />
                            )}
                            {/* Show store stock for pallet source */}
                            {reservationSource === 'pallet' && productBarcode && storeStock !== null && (
                                <p className="text-xs mt-1 flex items-center gap-1">
                                    <Package size={12} />
                                    Stoc în Magazin: <strong className={storeStock > 0 ? 'text-emerald-600' : 'text-zinc-400'}>
                                        {storeStock} buc
                                    </strong>
                                </p>
                            )}
                        </div>
                        <div>
                            <Label>Nume Produs</Label>
                            {productFound ? (
                                <div className="mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded-md text-zinc-700">
                                    {productName}
                                </div>
                            ) : (
                                <Input
                                    id="reservation-product-name"
                                    value={productName}
                                    onChange={(e) => {
                                        // Auto-capitalize first letter
                                        let value = e.target.value;
                                        if (value.length > 0) {
                                            value = value.charAt(0).toUpperCase() + value.slice(1);
                                        }
                                        setProductName(value);
                                    }}
                                    placeholder="Denumire produs"
                                    className="mt-1"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            document.getElementById('reservation-price')?.focus();
                                        }
                                    }}
                                />
                            )}
                        </div>
                        {/* Category hidden - no longer displayed */}
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={qty}
                                    onChange={(e) => setQty(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex-1">
                                <Label>Preț (RON)</Label>
                                <Input
                                    id="reservation-price"
                                    type="number"
                                    step="0.01"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    placeholder="0.00"
                                    className="mt-1"
                                    onFocus={(e) => e.target.select()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !isSubmitting) handleAddReservation();
                                    }}
                                />
                                {/* Purchase price min indicator (Achiziție) */}
                                {purchasePriceMin !== null && purchasePriceMin > 0 && (
                                    <p className="text-xs mt-1 text-blue-600 flex items-center gap-1">
                                        <span className="font-semibold">Achiziție Min:</span> 
                                        <span className="font-bold">{purchasePriceMin.toFixed(2)} RON</span>
                                    </p>
                                )}
                                {/* Min sold price indicator */}
                                {minSoldPrice !== null && minSoldPrice > 0 && (
                                    <p className="text-xs mt-1 text-amber-600 flex items-center gap-1">
                                        <span className="font-semibold">Vânzare Min:</span> 
                                        <span 
                                            className="font-bold cursor-pointer hover:underline"
                                            onClick={() => setPrice(minSoldPrice.toString())}
                                            title="Click pentru a folosi acest preț"
                                        >
                                            {minSoldPrice.toFixed(2)} RON
                                        </span>
                                        <span className="text-zinc-400">({timesSold}x)</span>
                                    </p>
                                )}
                                {/* Profit indicator */}
                                {purchasePriceMin > 0 && price && parseFloat(price) > 0 && (
                                    <p className={`text-xs mt-1 flex items-center gap-1 font-semibold ${parseFloat(price) > purchasePriceMin ? 'text-green-600' : 'text-red-600'}`}>
                                        <span>Profit Estimat:</span>
                                        <span>{((parseFloat(price) - purchasePriceMin) * parseInt(qty || 1)).toFixed(2)} RON</span>
                                        <span className="text-zinc-400">({((parseFloat(price) / purchasePriceMin - 1) * 100).toFixed(0)}%)</span>
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1" disabled={isSubmitting}>
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleAddReservation} 
                                className="flex-1 bg-teal-600 hover:bg-teal-700"
                                disabled={isSubmitting}
                            >
                                <Plus size={16} className="mr-2" />
                                {isSubmitting ? 'Se adaugă...' : 'Adaugă'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Editează Rezervare</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-zinc-600">{editingItem?.name}</p>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={editQty}
                                    onChange={(e) => setEditQty(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex-1">
                                <Label>Preț (RON)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={editPrice}
                                    onChange={(e) => setEditPrice(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">
                                Anulează
                            </Button>
                            <Button onClick={handleEditReservation} className="flex-1">
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Move to Another Customer Modal */}
            <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowRightLeft size={18} className="text-blue-500" />
                            Mută la Alt Client
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {movingItem && (
                            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                                <p className="font-medium text-zinc-800">{movingItem.name}</p>
                                <p className="text-xs text-zinc-500">
                                    {movingItem.quantity} x {formatPrice(movingItem.unit_price)} RON
                                </p>
                                <p className="text-xs text-zinc-400 mt-1">
                                    Client actual: <span className="font-medium">{movingItem.customer_id}</span>
                                </p>
                            </div>
                        )}
                        <div>
                            <Label>ID Client Destinație</Label>
                            <Input
                                value={targetCustomerId}
                                onChange={(e) => setTargetCustomerId(e.target.value.toUpperCase())}
                                placeholder="Ex: CLIENT456"
                                className="mt-1"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isMoving) handleMoveItem();
                                }}
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowMoveModal(false)} className="flex-1" disabled={isMoving}>
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleMoveItem} 
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                                disabled={!targetCustomerId.trim() || isMoving}
                            >
                                {isMoving ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                ) : (
                                    <ArrowRightLeft size={16} className="mr-2" />
                                )}
                                {isMoving ? 'Se mută...' : 'Mută'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Move ALL Reservations to Another Customer Modal */}
            <Dialog open={showMoveAllModal} onOpenChange={setShowMoveAllModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowRightLeft size={18} className="text-blue-500" />
                            Mută Toată Rezervarea
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {movingFromCustomer && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="font-medium text-blue-800">
                                    Client: {movingFromCustomer.customerId}
                                </p>
                                <p className="text-sm text-blue-600 mt-1">
                                    {movingFromCustomer.itemCount} produse • {formatPrice(movingFromCustomer.totalValue)} RON
                                </p>
                                <p className="text-xs text-blue-500 mt-2">
                                    Datele de rezervare vor fi păstrate.
                                </p>
                            </div>
                        )}
                        <div>
                            <Label>ID Client Destinație</Label>
                            <Input
                                value={targetCustomerIdAll}
                                onChange={(e) => setTargetCustomerIdAll(e.target.value.toUpperCase())}
                                placeholder="Ex: CLIENT456"
                                className="mt-1"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !isMoving) handleMoveAllToCustomer();
                                }}
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" onClick={() => setShowMoveAllModal(false)} className="flex-1" disabled={isMoving}>
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleMoveAllToCustomer} 
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                                disabled={!targetCustomerIdAll.trim() || isMoving}
                            >
                                {isMoving ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                ) : (
                                    <ArrowRightLeft size={16} className="mr-2" />
                                )}
                                {isMoving ? 'Se mută...' : 'Mută Toate'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Box Assignment Modal */}
            <Dialog open={showBoxModal} onOpenChange={(open) => {
                setShowBoxModal(open);
                if (!open) {
                    // User closed modal without selecting box - cancel the operation
                    setPendingReservation(null);
                    setBoxNumber('');
                    setIsSubmitting(false);
                }
            }}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Package size={20} />
                            Atribuie Cutie
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {pendingReservation && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                                <p className="font-bold text-amber-800 text-lg">
                                    Client: {pendingReservation.customerId}
                                </p>
                                <p className="text-sm text-amber-600 mt-1">
                                    În ce cutie punem produsele?
                                </p>
                            </div>
                        )}
                        
                        {freeBoxes.length > 0 && (
                            <div>
                                <Label className="text-xs text-zinc-500">Cutii libere:</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {freeBoxes.slice(0, 10).map(box => (
                                        <button
                                            key={box.id || `box-${box.box_number}`}
                                            type="button"
                                            onClick={() => setBoxNumber(box.box_number.toString())}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                boxNumber === box.box_number.toString()
                                                    ? 'bg-amber-500 text-white'
                                                    : 'bg-zinc-100 text-zinc-700 hover:bg-amber-100'
                                            }`}
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                    {/* F button for "fără cutie" */}
                                    <button
                                        type="button"
                                        onClick={() => setBoxNumber('F')}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                            boxNumber === 'F'
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                        }`}
                                        title="Fără cutie"
                                    >
                                        F
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {/* Quick buttons for M and L boxes */}
                        <div className="space-y-3">
                            {/* M Boxes */}
                            <div>
                                <Label className="text-xs text-zinc-500">Cutii M (Medium):</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {/* Free M boxes */}
                                    {freeMBoxes.map(box => (
                                        <button
                                            key={box.box_number}
                                            type="button"
                                            onClick={() => setBoxNumber(box.box_number)}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                boxNumber === box.box_number
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                            }`}
                                        >
                                            {box.box_number} ✓
                                        </button>
                                    ))}
                                    {/* Occupied M boxes - shown as disabled */}
                                    {occupiedMBoxes.map(box => (
                                        <button
                                            key={box.box_number}
                                            type="button"
                                            disabled
                                            title={`Ocupat de ${box.customer_id}`}
                                            className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-400 cursor-not-allowed line-through"
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                    {/* Next available M - suggestion */}
                                    {nextAvailable.m && (
                                        <button
                                            type="button"
                                            onClick={() => setBoxNumber(nextAvailable.m)}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all border-2 border-dashed ${
                                                boxNumber === nextAvailable.m
                                                    ? 'bg-blue-500 text-white border-blue-500'
                                                    : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                                            }`}
                                            title="Creează cutie nouă"
                                        >
                                            + {nextAvailable.m}
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            {/* L Boxes */}
                            <div>
                                <Label className="text-xs text-zinc-500">Cutii L (Large):</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {/* Free L boxes */}
                                    {freeLBoxes.map(box => (
                                        <button
                                            key={box.box_number}
                                            type="button"
                                            onClick={() => setBoxNumber(box.box_number)}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                boxNumber === box.box_number
                                                    ? 'bg-purple-500 text-white'
                                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                            }`}
                                        >
                                            {box.box_number} ✓
                                        </button>
                                    ))}
                                    {/* Occupied L boxes - shown as disabled */}
                                    {occupiedLBoxes.map(box => (
                                        <button
                                            key={box.box_number}
                                            type="button"
                                            disabled
                                            title={`Ocupat de ${box.customer_id}`}
                                            className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-400 cursor-not-allowed line-through"
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                    {/* Next available L - suggestion */}
                                    {nextAvailable.l && (
                                        <button
                                            type="button"
                                            onClick={() => setBoxNumber(nextAvailable.l)}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all border-2 border-dashed ${
                                                boxNumber === nextAvailable.l
                                                    ? 'bg-purple-500 text-white border-purple-500'
                                                    : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50'
                                            }`}
                                            title="Creează cutie nouă"
                                        >
                                            + {nextAvailable.l}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <Label>Număr Cutie (sau F pentru fără)</Label>
                            <Input
                                value={boxNumber}
                                onChange={(e) => setBoxNumber(e.target.value.toUpperCase())}
                                placeholder="Ex: 1, M1, L1, F..."
                                className="mt-1 text-center text-2xl font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAssignBox();
                                }}
                            />
                            <p className="text-xs text-zinc-500 mt-1 text-center">
                                Numere: 1,2,3... | M1,M2... (Medium) | L1,L2... (Large) | F = Fără cutie
                            </p>
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowBoxModal(false);
                                    setPendingReservation(null);
                                    setBoxNumber('');
                                    setIsSubmitting(false);
                                    toast.info('Rezervare anulată');
                                }} 
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleAssignBox} 
                                className="flex-1 bg-amber-500 hover:bg-amber-600"
                                disabled={!boxNumber}
                            >
                                <Package size={16} className="mr-2" />
                                Confirmă Cutia
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Edit Box Modal */}
            <Dialog open={showEditBoxModal} onOpenChange={setShowEditBoxModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-blue-600">
                            <Box size={20} />
                            Editează Cutia
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {editingBoxCustomer && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                                <p className="font-bold text-blue-800 text-lg">
                                    Client: {editingBoxCustomer}
                                </p>
                                <p className="text-sm text-blue-600 mt-1">
                                    {customerBoxes[editingBoxCustomer]?.box_message || 'Fără cutie atribuită'}
                                </p>
                            </div>
                        )}
                        {editOldBox && (
                            <div data-testid="edit-box-select-old">
                                <Label className="text-xs text-zinc-500">Alege cutia pe care o modifici:</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {(customerBoxes[editingBoxCustomer]?.boxes || [])
                                        .filter(b => String(b.box_number).toUpperCase() !== 'F')
                                        .map(b => {
                                            const num = String(b.box_number);
                                            return (
                                                <button
                                                    key={`old-${num}`}
                                                    data-testid={`edit-box-old-${num}`}
                                                    onClick={() => { setEditOldBox(num); setEditBoxNumber(num); }}
                                                    className={`px-3 py-1 rounded text-sm font-bold border transition-colors ${editOldBox === num ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
                                                >
                                                    {num}
                                                </button>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                        
                        {freeBoxes.length > 0 && (
                            <div>
                                <Label className="text-xs text-zinc-500">Cutii numerotate libere:</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {freeBoxes.slice(0, 10).map(box => (
                                        <button
                                            key={box.id || `box-${box.box_number}`}
                                            type="button"
                                            onClick={() => setEditBoxNumber(box.box_number.toString())}
                                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                                editBoxNumber === box.box_number.toString()
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-zinc-100 text-zinc-700 hover:bg-blue-100'
                                            }`}
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                    {/* F button */}
                                    <button
                                        type="button"
                                        onClick={() => setEditBoxNumber('F')}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                            editBoxNumber === 'F'
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                        }`}
                                        title="Fără cutie"
                                    >
                                        F
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {/* M/L Boxes Section */}
                        <div className="space-y-2">
                            <Label className="text-xs text-zinc-500">Cutii M / L:</Label>
                            <div className="flex flex-wrap gap-2">
                                {/* Free M */}
                                {freeMBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        type="button"
                                        onClick={() => setEditBoxNumber(box.box_number)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                            editBoxNumber === box.box_number
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                        }`}
                                    >
                                        {box.box_number} ✓
                                    </button>
                                ))}
                                {/* Occupied M */}
                                {occupiedMBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        type="button"
                                        disabled
                                        title={`Ocupat: ${box.customer_id}`}
                                        className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-400 cursor-not-allowed line-through"
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {/* Next M */}
                                {nextAvailable.m && (
                                    <button
                                        type="button"
                                        onClick={() => setEditBoxNumber(nextAvailable.m)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium border-2 border-dashed ${
                                            editBoxNumber === nextAvailable.m
                                                ? 'bg-blue-500 text-white border-blue-500'
                                                : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                                        }`}
                                    >
                                        + {nextAvailable.m}
                                    </button>
                                )}
                                {/* Free L */}
                                {freeLBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        type="button"
                                        onClick={() => setEditBoxNumber(box.box_number)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                                            editBoxNumber === box.box_number
                                                ? 'bg-purple-500 text-white'
                                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        }`}
                                    >
                                        {box.box_number} ✓
                                    </button>
                                ))}
                                {/* Occupied L */}
                                {occupiedLBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        type="button"
                                        disabled
                                        title={`Ocupat: ${box.customer_id}`}
                                        className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-400 cursor-not-allowed line-through"
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {/* Next L */}
                                {nextAvailable.l && (
                                    <button
                                        type="button"
                                        onClick={() => setEditBoxNumber(nextAvailable.l)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium border-2 border-dashed ${
                                            editBoxNumber === nextAvailable.l
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50'
                                        }`}
                                    >
                                        + {nextAvailable.l}
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div>
                            <Label>Număr Cutie (sau F pentru fără cutie)</Label>
                            <Input
                                value={editBoxNumber}
                                onChange={(e) => setEditBoxNumber(e.target.value.toUpperCase())}
                                placeholder="Ex: 1, 2, F..."
                                className="mt-1 text-center text-2xl font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBox();
                                }}
                            />
                            <p className="text-xs text-zinc-500 mt-1 text-center">
                                Folosește "F" dacă clientul nu are cutie fizică
                            </p>
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowEditBoxModal(false);
                                    setEditingBoxCustomer(null);
                                }} 
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleSaveBox} 
                                className="flex-1 bg-blue-500 hover:bg-blue-600"
                                disabled={!editBoxNumber.trim()}
                            >
                                <Box size={16} className="mr-2" />
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Extra Box Modal */}
            <Dialog open={showAddExtraBoxModal} onOpenChange={setShowAddExtraBoxModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Plus className="text-green-500" size={20} />
                            Adaugă Cutie Suplimentară - {extraBoxCustomer}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Current boxes info */}
                        {extraBoxCustomer && customerBoxes[extraBoxCustomer] && (
                            <div className="p-3 bg-blue-50 rounded-lg text-sm">
                                <span className="text-blue-600 font-medium">
                                    Cutii curente: {customerBoxes[extraBoxCustomer].box_message || 'Niciuna'}
                                </span>
                            </div>
                        )}

                        {/* Free boxes quick select */}
                        {extraBoxFreeBoxes.length > 0 && (
                            <div>
                                <Label className="text-zinc-500 text-xs">Cutii Libere (click pentru selectare):</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {extraBoxFreeBoxes.map(box => (
                                        <button
                                            key={box.box_number}
                                            onClick={() => setExtraBoxNumber(String(box.box_number))}
                                            className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                                extraBoxNumber === String(box.box_number)
                                                    ? 'bg-green-500 text-white border-green-500'
                                                    : 'bg-zinc-100 text-zinc-700 border-zinc-200 hover:border-green-400'
                                            }`}
                                        >
                                            {box.box_number}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* M/L boxes section */}
                        <div>
                            <Label className="text-zinc-500 text-xs">Cutii M / L:</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {/* Show occupied M boxes that belong to this customer */}
                                {occupiedMBoxes.filter(b => b.customer_id === extraBoxCustomer).map(box => (
                                    <button
                                        key={box.box_number}
                                        className="px-3 py-2 rounded-lg border-2 font-bold bg-green-500 text-white border-green-500 opacity-50 cursor-not-allowed"
                                        disabled
                                    >
                                        ✓ {box.box_number}
                                    </button>
                                ))}
                                {/* Free M boxes */}
                                {freeMBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        onClick={() => setExtraBoxNumber(String(box.box_number))}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            extraBoxNumber === String(box.box_number)
                                                ? 'bg-amber-500 text-white border-amber-500'
                                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'
                                        }`}
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {/* Next available M */}
                                {nextAvailable.m && (
                                    <button
                                        onClick={() => setExtraBoxNumber(nextAvailable.m)}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            extraBoxNumber === nextAvailable.m
                                                ? 'bg-amber-500 text-white border-amber-500'
                                                : 'bg-amber-100 text-amber-700 border-amber-300 hover:border-amber-500'
                                        }`}
                                    >
                                        + {nextAvailable.m}
                                    </button>
                                )}
                                {/* Occupied L boxes that belong to this customer */}
                                {occupiedLBoxes.filter(b => b.customer_id === extraBoxCustomer).map(box => (
                                    <button
                                        key={box.box_number}
                                        className="px-3 py-2 rounded-lg border-2 font-bold bg-purple-500 text-white border-purple-500 opacity-50 cursor-not-allowed"
                                        disabled
                                    >
                                        ✓ {box.box_number}
                                    </button>
                                ))}
                                {/* Free L boxes */}
                                {freeLBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        onClick={() => setExtraBoxNumber(String(box.box_number))}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            extraBoxNumber === String(box.box_number)
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-purple-50 text-purple-700 border-purple-200 hover:border-purple-400'
                                        }`}
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {/* Next available L */}
                                {nextAvailable.l && (
                                    <button
                                        onClick={() => setExtraBoxNumber(nextAvailable.l)}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            extraBoxNumber === nextAvailable.l
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-purple-100 text-purple-700 border-purple-300 hover:border-purple-500'
                                        }`}
                                    >
                                        + {nextAvailable.l}
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        <div>
                            <Label>Număr Cutie Suplimentară</Label>
                            <Input
                                value={extraBoxNumber}
                                onChange={(e) => setExtraBoxNumber(e.target.value.toUpperCase())}
                                placeholder="Ex: 5, 10..."
                                className="mt-1 text-center text-2xl font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAddExtraBox();
                                }}
                            />
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowAddExtraBoxModal(false);
                                    setExtraBoxCustomer(null);
                                }} 
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleAddExtraBox} 
                                className="flex-1 bg-green-500 hover:bg-green-600"
                                disabled={!extraBoxNumber.trim()}
                            >
                                <Plus size={16} className="mr-2" />
                                Adaugă Cutie
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Client Modal */}
            <Dialog open={showEditClientModal} onOpenChange={setShowEditClientModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Editează Date Client
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Nume Client</Label>
                            <Input
                                value={editClientName}
                                onChange={(e) => setEditClientName(e.target.value)}
                                placeholder="Numele clientului"
                            />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2">
                                <Phone size={14} />
                                Telefon
                                {!editClientPhone && (
                                    <span className="text-xs text-amber-600">(necesar pentru portal)</span>
                                )}
                            </Label>
                            <Input
                                value={editClientPhone}
                                onChange={(e) => setEditClientPhone(e.target.value)}
                                placeholder="07XX XXX XXX"
                                className={!editClientPhone ? "border-amber-300 focus:border-amber-500" : ""}
                            />
                        </div>
                        <div>
                            <Label>Email (opțional)</Label>
                            <Input
                                type="email"
                                value={editClientEmail}
                                onChange={(e) => setEditClientEmail(e.target.value)}
                                placeholder="email@exemplu.ro"
                            />
                        </div>
                        <div>
                            <Label>Adresă (opțional)</Label>
                            <Input
                                value={editClientAddress}
                                onChange={(e) => setEditClientAddress(e.target.value)}
                                placeholder="Adresa clientului"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setShowEditClientModal(false)}
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={handleSaveClient}
                                disabled={savingClient}
                                className="bg-violet-600 hover:bg-violet-700"
                            >
                                {savingClient ? 'Se salvează...' : 'Salvează'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Copy Text Modal for Mobile */}
            <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
                <DialogContent className="sm:max-w-md max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Copy className="text-teal-500" size={20} />
                            Copiază Lista
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-2">
                        <p className="text-sm text-zinc-500">
                            Ține apăsat pe text → Selectează tot → Copiază
                        </p>
                        
                        <textarea
                            value={copyText}
                            readOnly
                            className="w-full h-48 p-3 text-sm border border-zinc-200 rounded-lg bg-zinc-50 font-mono"
                            onClick={(e) => {
                                e.target.select();
                                e.target.setSelectionRange(0, copyText.length);
                            }}
                        />
                        
                        <div className="flex gap-3">
                            <Button 
                                variant="outline" 
                                onClick={() => setShowCopyModal(false)} 
                                className="flex-1"
                            >
                                Închide
                            </Button>
                            <Button 
                                onClick={async () => {
                                    if (navigator.share) {
                                        try {
                                            await navigator.share({ text: copyText });
                                            setShowCopyModal(false);
                                        } catch (err) {
                                            // User cancelled
                                        }
                                    }
                                }}
                                className="flex-1 bg-teal-500 hover:bg-teal-600"
                            >
                                Trimite
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Phone Required Modal - shown when adding product to customer without phone */}
            <Dialog open={showPhoneRequiredModal} onOpenChange={setShowPhoneRequiredModal}>
                <DialogContent className="sm:max-w-md z-[200]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <Phone size={20} />
                            Telefon necesar
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-zinc-600">
                            Clientul <strong>{phoneRequiredCustomerId}</strong> nu are număr de telefon. 
                            Introdu telefonul pentru a continua:
                        </p>
                        <Input
                            type="tel"
                            placeholder="07xxxxxxxx"
                            value={phoneRequiredInput}
                            onChange={(e) => setPhoneRequiredInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && phoneRequiredInput.trim()) {
                                    handleSavePhoneRequired();
                                }
                            }}
                            autoFocus
                            className="text-lg"
                        />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowPhoneRequiredModal(false);
                                setPhoneRequiredInput('');
                            }}
                        >
                            Mai târziu
                        </Button>
                        <Button
                            onClick={handleSavePhoneRequired}
                            disabled={!phoneRequiredInput.trim()}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            Salvează telefon
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Virtual Client Box Assignment Modal */}
            <Dialog open={showVirtualBoxModal} onOpenChange={setShowVirtualBoxModal}>
                <DialogContent className="sm:max-w-md z-[9999]" style={{ zIndex: 9999 }} data-testid="virtual-box-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-violet-600">
                            <Box size={20} />
                            Alocă Cutie - Client Local
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-zinc-600">
                            Client: <strong className="text-violet-700">{boxAssignCustomerId}</strong>
                        </p>
                        <div>
                            <Label className="text-sm font-medium">Număr Cutie (M1, L2, etc.)</Label>
                            <Input
                                data-testid="virtual-box-input"
                                value={boxInput}
                                onChange={(e) => setBoxInput(e.target.value.toUpperCase())}
                                placeholder="ex: M1, L2, A3"
                                className="mt-1 text-lg font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && boxInput.trim()) {
                                        handleAssignVirtualBox();
                                    }
                                }}
                            />
                        </div>
                        {/* Quick select M/L boxes */}
                        <div>
                            <Label className="text-zinc-500 text-xs">Cutii M / L:</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {freeMBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        onClick={() => setBoxInput(String(box.box_number))}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            boxInput === String(box.box_number)
                                                ? 'bg-amber-500 text-white border-amber-500'
                                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'
                                        }`}
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {nextAvailable.m && (
                                    <button
                                        onClick={() => setBoxInput(nextAvailable.m)}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            boxInput === nextAvailable.m
                                                ? 'bg-amber-500 text-white border-amber-500'
                                                : 'bg-amber-100 text-amber-700 border-amber-300 hover:border-amber-500'
                                        }`}
                                    >
                                        + {nextAvailable.m}
                                    </button>
                                )}
                                {freeLBoxes.map(box => (
                                    <button
                                        key={box.box_number}
                                        onClick={() => setBoxInput(String(box.box_number))}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            boxInput === String(box.box_number)
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-purple-50 text-purple-700 border-purple-200 hover:border-purple-400'
                                        }`}
                                    >
                                        {box.box_number}
                                    </button>
                                ))}
                                {nextAvailable.l && (
                                    <button
                                        onClick={() => setBoxInput(nextAvailable.l)}
                                        className={`px-3 py-2 rounded-lg border-2 font-bold transition-colors ${
                                            boxInput === nextAvailable.l
                                                ? 'bg-purple-500 text-white border-purple-500'
                                                : 'bg-purple-100 text-purple-700 border-purple-300 hover:border-purple-500'
                                        }`}
                                    >
                                        + {nextAvailable.l}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowVirtualBoxModal(false);
                                setBoxInput('');
                            }}
                        >
                            Anulează
                        </Button>
                        <Button
                            data-testid="virtual-box-submit"
                            onClick={handleAssignVirtualBox}
                            disabled={!boxInput.trim()}
                            className="bg-violet-600 hover:bg-violet-700"
                        >
                            <Box size={16} className="mr-1" />
                            Alocă
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RezervariV2;
